import { logDev } from '../utils/LogDev';

export class EditorManager {
    private magnifier: HTMLDivElement | null = null;
    private magCanvas: HTMLCanvasElement | null = null;
    private magCtx: CanvasRenderingContext2D | null = null;

    constructor(private app: any) {
        // Expose critical methods to the app instance for cross-component access
        this.app.renderToolPreview = this.renderToolPreview.bind(this);
        this.app.updatePasteOverlay = this.updatePasteOverlay.bind(this);
    }

    private initMagnifier() {
        if (this.magnifier) return;
        
        const ZOOM = 36;
        const PIXELS = 5;
        const SIZE = ZOOM * PIXELS;

        this.magnifier = document.createElement('div');
        this.magnifier.id = 'sl-magnifier';
        this.magnifier.style.position = 'fixed';
        this.magnifier.style.width = `${SIZE}px`;
        this.magnifier.style.height = `${SIZE}px`;
        this.magnifier.style.borderRadius = '50%';
        this.magnifier.style.border = '4px solid #555';
        this.magnifier.style.boxShadow = '0 0 10px rgba(0,0,0,0.5)';
        this.magnifier.style.pointerEvents = 'none';
        this.magnifier.style.display = 'none';
        this.magnifier.style.zIndex = '999999';
        this.magnifier.style.overflow = 'hidden';
        this.magnifier.style.backgroundImage = 'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPjxyZWN0IHdpZHRoPSI0IiBoZWlnaHQ9IjQiIGZpbGw9IiNjY2MiLz48cmVjdCB4PSI0IiB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIi8+PHJlY3QgeT0iNCIgd2lkdGg9IjQiIGhlaWdodD0iNCIgZmlsbD0iI2ZmZiIvPjxyZWN0IHg9IjQiIHk9IjQiIHdpZHRoPSI0IiBoZWlnaHQ9IjQiIGZpbGw9IiNjY2MiLz48L3N2Zz4=")';

        this.magCanvas = document.createElement('canvas');
        this.magCanvas.width = SIZE;
        this.magCanvas.height = SIZE;
        this.magCanvas.style.width = '100%';
        this.magCanvas.style.height = '100%';
        this.magCanvas.style.imageRendering = 'pixelated';
        this.magnifier.appendChild(this.magCanvas);

        this.app.container.appendChild(this.magnifier);
        this.magCtx = this.magCanvas.getContext('2d');
        if (this.magCtx) this.magCtx.imageSmoothingEnabled = false;
    }

    setupToolbar() {
        this.initMagnifier();

        // Basic Tools
        const toolButtons = this.app.container.querySelectorAll('.sl-tool-btn[data-tool]');

        toolButtons.forEach((btn: any) => {
            btn.addEventListener('click', (e: any) => {
                e.stopPropagation(); // Prevent container click from immediately closing/resetting
                const tool = (btn as HTMLElement).dataset.tool!;
                this.switchTool(tool);
            });
        });

        // Keyboard shortcuts
        document.addEventListener('keydown', async (e) => {
            if (!this.app.container.style.display || this.app.container.style.display === 'none') return;

            const key = e.key?.toLowerCase() || '';

            // Escape and Enter should work without Ctrl
            if (e.key === 'Enter' && this.app.pastedData) {
                e.preventDefault();
                this.app.clipboardManager.commitPaste();
                return;
            }

            if (e.key === 'Escape') {
                let handled = false;
                if (this.app.pastedData) {
                    e.preventDefault();
                    this.app.clipboardManager.commitPaste();
                    logDev('[EditorManager] Escape key: Committed floating selection');
                    handled = true;
                }
                if (this.app.selectedFrame) {
                    e.preventDefault();
                    this.app.selectedFrame = null;
                    const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
                    const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
                    const fw = parseInt(wInput?.value || '32');
                    const fh = parseInt(hInput?.value || '32');
                    this.updateSelectionHighlight(fw, fh);
                    logDev('[EditorManager] Escape key: Unselected frame');
                    handled = true;
                }
                if (handled) return;
            }

            // Require Ctrl key for all other shortcuts
            if (!e.ctrlKey) return;

            // Undo/Redo
            if (key === 'z') {
                e.preventDefault();
                if (e.shiftKey) {
                    await this.app.historyManager.redo();
                } else {
                    await this.app.historyManager.undo();
                }
                return;
            }

            if (key === 'y') {
                e.preventDefault();
                await this.app.historyManager.redo();
                return;
            }

            // Transformation Shortcuts (Ctrl + Shift)
            if (e.ctrlKey && e.shiftKey) {
                if (key === 'r') {
                    e.preventDefault();
                    this.app.applyRotate();
                    return;
                }
                if (key === 'h') {
                    e.preventDefault();
                    this.app.applyFlip('horizontal');
                    return;
                }
                if (key === 'v') {
                    e.preventDefault();
                    this.app.applyFlip('vertical');
                    return;
                }
                if (key === 't') {
                    e.preventDefault();
                    this.app.applyTranspose();
                    return;
                }
                if (key === 'b') {
                    e.preventDefault();
                    this.switchTool('grid');
                    return;
                }
            }

            const shortcuts: Record<string, string> = {
                'm': 'mouse',
                'b': 'brush',
                'e': 'eraser',
                'f': 'bucket'
            };


            if (shortcuts[key]) {
                e.preventDefault();
                this.switchTool(shortcuts[key]);
            }

            // Handle Brush Size shortcuts (Ctrl + +/-)
            if (key === '+' || key === '=' || key === '-') {
                e.preventDefault();
                const currentSize = this.app.toolState.brush.size;
                if (key === '+' || key === '=') {
                    updateSize(currentSize + 1);
                } else if (key === '-') {
                    updateSize(currentSize - 1);
                }
                return;
            }

            // Copy/Cut/Paste
            if (key === 'c') {
                e.preventDefault();
                this.app.clipboardManager.copyToClipboard();
                return;
            }
            if (key === 'x') {
                if (e.target && (e.target as HTMLElement).tagName === 'INPUT') return;
                // First check if selection tool is active, if not fallback to switch colors
                if (this.app.clipboardManager.hasSelection()) {
                    e.preventDefault();
                    this.app.clipboardManager.cutToClipboard();
                    return;
                } else {
                    e.preventDefault();
                    const brushColorSwitch = this.app.container.querySelector('#sl-brush-color-switch') as HTMLButtonElement;
                    if (brushColorSwitch) brushColorSwitch.click();
                    return;
                }
            }
            if (key === 'v') {
                e.preventDefault();
                this.app.clipboardManager.pasteFromClipboard();
                return;
            }

            // Explorer Shortcuts
            if (key === 'n') {
                e.preventDefault();
                if (e.shiftKey) {
                    // New Sprite (Ctrl + Shift + N)
                    if (this.app.projectId) {
                        (this.app.container.querySelector('#sl-modal-create-sprite') as HTMLElement).style.display = 'flex';
                        const input = this.app.container.querySelector('#sl-new-sprite-name') as HTMLInputElement;
                        if (input) {
                            input.value = '';
                            input.focus();
                        }
                    } else {
                        alert(this.app.t('err_select_project'));
                    }
                } else {
                    // New Project (Ctrl + N)
                    (this.app.container.querySelector('#sl-modal-create-project') as HTMLElement).style.display = 'flex';
                    const input = this.app.container.querySelector('#sl-new-project-name') as HTMLInputElement;
                    if (input) {
                        input.value = '';
                        input.focus();
                    }
                }
                return;
            }

        });

        // Click outside the canvas logic to commit paste or deselect frame
        document.addEventListener('pointerdown', (e) => {
            const canvas = this.app.container.querySelector('#sl-sheet-canvas') as HTMLElement;
            const trash = this.app.container.querySelector('#sl-trash-bin') as HTMLElement;
            const toolbar = this.app.container.querySelector('#sl-toolbar-panel') as HTMLElement;
            const brushSettings = this.app.container.querySelector('#sl-brush-settings') as HTMLElement;
            const sidebar = this.app.container.querySelector('#sl-sidebar-left') as HTMLElement;
            const header = this.app.container.querySelector('#navbar') as HTMLElement;

            const target = e.target as Node;
            const isOutside = (
                (canvas && !canvas.contains(target)) &&
                !(trash && trash.contains(target)) &&
                !(toolbar && toolbar.contains(target)) &&
                !(brushSettings && brushSettings.contains(target)) &&
                !(sidebar && sidebar.contains(target)) &&
                !(header && header.contains(target))
            );

            if (isOutside) {
                if (this.app.pastedData) {
                    this.app.clipboardManager.commitPaste();
                }
                if (this.app.selectedFrame) {
                    this.app.selectedFrame = null;
                    const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
                    const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
                    const fw = parseInt(wInput?.value || '32');
                    const fh = parseInt(hInput?.value || '32');
                    this.updateSelectionHighlight(fw, fh);
                    logDev('[EditorManager] Clickaway: Unselected frame');
                }
            }
        });

        // Shared Brush Settings
        const brushColor = this.app.container.querySelector('#sl-brush-color') as HTMLElement;
        const brushColorSecondary = this.app.container.querySelector('#sl-brush-color-secondary') as HTMLElement;
        const brushColorSwitch = this.app.container.querySelector('#sl-brush-color-switch') as HTMLButtonElement;

        brushColorSwitch?.addEventListener('click', () => {
            if (!brushColor || !brushColorSecondary) return;

            // Swap from toolState (always hex) instead of element.style.backgroundColor (which returns rgb() strings)
            const tempPrimary = this.app.toolState.brush.color;
            const tempSecondary = this.app.toolState.brush.secondaryColor;

            this.app.toolState.brush.color = tempSecondary;
            this.app.toolState.brush.secondaryColor = tempPrimary;
            this.app.toolState.eraser.color = tempSecondary;
            this.app.toolState.eraser.secondaryColor = tempPrimary;

            // Update UI to match
            brushColor.style.backgroundColor = tempSecondary;
            brushColorSecondary.style.backgroundColor = tempPrimary;

            this.app.updateCursor();
            this.app.historyManager.takeSnapshot();
        });

        const brushSize = this.app.container.querySelector('#sl-brush-size') as HTMLInputElement;
        const brushSizeInput = this.app.container.querySelector('#sl-brush-size-input') as HTMLInputElement;
        const brushSizeDisplay = this.app.container.querySelector('#sl-brush-size-display') as HTMLSpanElement;
        const brushSizeContainer = this.app.container.querySelector('#sl-brush-size-container') as HTMLDivElement;

        if (brushSize) brushSize.setAttribute('title', this.app.t('tool_brush_size') || 'Adjust Brush Size');
        if (brushSizeContainer) brushSizeContainer.setAttribute('title', this.app.t('tool_brush_size') || 'Manual Brush Size');

        const updateSize = (size: number) => {
            size = Math.max(1, Math.min(100, size));
            this.app.toolState.brush.size = size;
            this.app.toolState.eraser.size = size;
            if (brushSize) brushSize.value = size.toString();
            if (brushSizeInput) brushSizeInput.value = size.toString();
            if (brushSizeDisplay) brushSizeDisplay.innerText = size + ' px';
            if (brushSize) this.app.updateSliderFill(brushSize);
            this.app.updateCursor();

            // Refresh preview immediately if mouse is over canvas
            if (this.app.lastSheetX !== -1) {
                const canvas = this.app.container.querySelector('#sl-sheet-canvas') as HTMLCanvasElement;
                if (canvas) {
                    this.app.renderToolPreview(this.app.lastSheetX, this.app.lastSheetY, canvas, this.app.dataCanvas);
                }
            }
        };

        brushSizeContainer?.addEventListener('click', (e: MouseEvent) => {
            if (e.target === brushSizeInput) return;
            if (brushSizeInput && brushSizeDisplay) {
                brushSizeDisplay.style.display = 'none';
                brushSizeInput.style.display = 'block';
                setTimeout(() => {
                    brushSizeInput.focus();
                    brushSizeInput.select();
                }, 10);
            }
        });

        brushSize?.addEventListener('input', (e: any) => {
            updateSize(parseInt((e.target as HTMLInputElement).value));
        });

        const bucketTolerance = this.app.container.querySelector('#sl-bucket-tolerance') as HTMLInputElement;
        const bucketToleranceInput = this.app.container.querySelector('#sl-bucket-tolerance-input') as HTMLInputElement;
        const bucketToleranceDisplay = this.app.container.querySelector('#sl-bucket-tolerance-display') as HTMLSpanElement;
        const bucketToleranceContainer = this.app.container.querySelector('#sl-bucket-tolerance-container') as HTMLDivElement;

        const updateTolerance = (val: number) => {
            val = Math.max(0, Math.min(100, val));
            this.app.toolState.bucket.tolerance = val;
            if (bucketTolerance) bucketTolerance.value = val.toString();
            if (bucketToleranceInput) bucketToleranceInput.value = val.toString();
            if (bucketToleranceDisplay) bucketToleranceDisplay.innerText = val + '%';
            if (bucketTolerance) this.app.updateSliderFill(bucketTolerance);
        };

        bucketToleranceContainer?.addEventListener('click', (e: MouseEvent) => {
            if (e.target === bucketToleranceInput) return;
            if (bucketToleranceInput && bucketToleranceDisplay) {
                bucketToleranceDisplay.style.display = 'none';
                bucketToleranceInput.style.display = 'block';
                setTimeout(() => {
                    bucketToleranceInput.focus();
                    bucketToleranceInput.select();
                }, 10);
            }
        });

        const finishToleranceEditing = () => {
            if (bucketToleranceInput && bucketToleranceDisplay) {
                const val = parseInt(bucketToleranceInput.value);
                updateTolerance(val);
                bucketToleranceInput.style.display = 'none';
                bucketToleranceDisplay.style.display = 'block';
            }
        };

        bucketToleranceInput?.addEventListener('keydown', (e: any) => {
            if (e.key === 'Enter') finishToleranceEditing();
        });

        bucketToleranceInput?.addEventListener('blur', finishToleranceEditing);

        bucketToleranceInput?.addEventListener('input', (e: any) => {
            const val = parseInt((e.target as HTMLInputElement).value);
            if (!isNaN(val)) {
                updateTolerance(val);
            }
        });

        bucketTolerance?.addEventListener('input', (e: any) => {
            updateTolerance(parseInt((e.target as HTMLInputElement).value));
        });

        // Initialize all sliders with the premium fill effect
        this.app.container.querySelectorAll('.sl-layout-32').forEach((slider: any) => {
            this.app.updateSliderFill(slider as HTMLInputElement);
        });

        const finishEditing = () => {
            if (brushSizeInput && brushSizeDisplay) {
                const val = parseInt(brushSizeInput.value);
                updateSize(val);
                brushSizeInput.style.display = 'none';
                brushSizeDisplay.style.display = 'block';
            }
        };

        brushSizeInput?.addEventListener('keydown', (e: any) => {
            if (e.key === 'Enter') finishEditing();
        });

        brushSizeInput?.addEventListener('blur', finishEditing);

        brushSizeInput?.addEventListener('input', (e: any) => {
            const val = parseInt((e.target as HTMLInputElement).value);
            if (!isNaN(val)) {
                updateSize(val);
            }
        });

        const modeToggle = this.app.container.querySelector('#sl-brush-mode-toggle') as HTMLButtonElement;
        modeToggle?.addEventListener('click', () => {
            const currentMode = this.app.toolState.brush.mode;
            const newMode = currentMode === 'color' ? 'transparent' : 'color';

            this.app.toolState.brush.mode = newMode;
            this.app.toolState.eraser.mode = newMode === 'transparent' ? 'transparent' : 'color';

            const icon = modeToggle.querySelector('i') as HTMLElement;

            // Update UI
            if (newMode === 'transparent') {
                if (icon) {
                    icon.className = 'fa-solid fa-droplet-slash';
                }
                modeToggle.style.background = '#0078d4';
                modeToggle.style.borderColor = '#818cf8';
                modeToggle.style.color = 'white';
            } else {
                if (icon) {
                    icon.className = 'fa-solid fa-droplet';
                }
                modeToggle.style.background = '#1f1f1f';
                modeToggle.style.borderColor = '#3c3c3c';
                modeToggle.style.color = '#9d9d9d';
            }
        });

        const eyedropperBtn = this.app.container.querySelector('#sl-eyedropper-btn') as HTMLButtonElement;
        eyedropperBtn?.addEventListener('click', () => {
            this.switchTool('picker');
        });

        // Scale tool
        const scaleApply = this.app.container.querySelector('#sl-scale-apply');
        scaleApply?.addEventListener('click', () => {
            const w = parseInt((this.app.container.querySelector('#sl-scale-width') as HTMLInputElement).value);
            const h = parseInt((this.app.container.querySelector('#sl-scale-height') as HTMLInputElement).value);
            this.applyScale(w, h);
        });

        // Flip tools (Immediate Action)
        const flipHBtn = this.app.container.querySelector('#sl-action-flip-h');
        const flipVBtn = this.app.container.querySelector('#sl-action-flip-v');

        flipHBtn?.addEventListener('click', () => {
            this.applyFlip('horizontal');
        });
        flipVBtn?.addEventListener('click', () => {
            this.applyFlip('vertical');
        });

        // Rotate tool - click to rotate 90° clockwise
        const rotateBtn = this.app.container.querySelector('#sl-action-rotate');
        rotateBtn?.addEventListener('click', () => {
            this.applyRotate();
        });

        // Transpose tool - click to switch orientation
        const transposeBtn = this.app.container.querySelector('#sl-action-transpose-real');
        transposeBtn?.addEventListener('click', () => {
            this.applyTranspose();
        });

        // Initialize draggables
        const toolbar = this.app.container.querySelector('#sl-toolbar-panel') as HTMLElement;
        const grip = this.app.container.querySelector('#sl-toolbar-grip') as HTMLElement;
        const container = this.app.container.querySelector('#sl-sheet-container') as HTMLElement;

        if (toolbar && grip && container) {
            this.makeDraggable(toolbar, toolbar, container, 'sl-toolbar-pos');
        }

        const brushSettings = this.app.container.querySelector('#sl-brush-settings') as HTMLElement;
        if (brushSettings && container) {
            // No grip, drag by background/labels
            this.makeDraggable(brushSettings, brushSettings, container, 'sl-brush-settings-pos-v2');
        }

        const colorPicker = this.app.container.querySelector('#sl-color-picker') as HTMLElement;
        if (colorPicker) {
            // Drag by header or main area
            const header = colorPicker.querySelector('.sl-cp-header') as HTMLElement;
            // No container = free movement everywhere
            this.makeDraggable(colorPicker, header || colorPicker, null as any); 
        }
    }

    private makeDraggable(element: HTMLElement, handle: HTMLElement, container: HTMLElement | null, storageKey?: string) {
        let isDragging = false;
        let startX = 0;
        let startY = 0;
        let initialRect: DOMRect;

        // Let's stick to a simpler, more robust viewport-to-parent math:
        const onPointerMove = (e: PointerEvent) => {
            if (!isDragging) return;

            const dx = e.clientX - startX;
            const dy = e.clientY - startY;

            let targetViewportTop = initialRect.top + dy;
            let targetViewportLeft = initialRect.left + dx;

            const currentParentRect = (element.offsetParent as HTMLElement || document.body).getBoundingClientRect();

            // Clamp viewport coordinates only if container is provided
            if (container) {
                const boundaryRect = container.getBoundingClientRect();
                targetViewportTop = Math.max(boundaryRect.top + 8, Math.min(boundaryRect.bottom - initialRect.height - 8, targetViewportTop));
                targetViewportLeft = Math.max(boundaryRect.left + 8, Math.min(boundaryRect.right - initialRect.width - 8, targetViewportLeft));
            } else {
                // Application-wide clamping (at least don't move off screen)
                targetViewportTop = Math.max(8, Math.min(window.innerHeight - initialRect.height - 8, targetViewportTop));
                targetViewportLeft = Math.max(8, Math.min(window.innerWidth - initialRect.width - 8, targetViewportLeft));
            }

            let finalL = targetViewportLeft;
            let finalT = targetViewportTop;

            if (element.id === 'sl-toolbar-panel' && container) {
                const infoContainer = this.app.container.querySelector('#sl-sheet-info-container') as HTMLElement;
                if (infoContainer) {
                    const infoRect = infoContainer.getBoundingClientRect();
                    if (finalL < (infoRect.right + 8) && finalT < (infoRect.bottom + 8)) {
                        // Push it below or to the right? Usually better to keep it to the right of the buttons
                        finalL = infoRect.right + 16;
                    }
                }
            }

            // Convert back to parent-relative
            element.style.bottom = 'auto';
            element.style.left = 'auto';
            element.style.top = `${finalT - currentParentRect.top}px`;
            element.style.right = `${currentParentRect.right - (finalL + initialRect.width)}px`;
        };

        const stopDrag = () => {
            if (!isDragging) return;
            isDragging = false;
            element.style.transition = '';
            
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', stopDrag);
            window.removeEventListener('pointercancel', stopDrag);

            if (storageKey) {
                localStorage.setItem(storageKey, JSON.stringify({
                    top: element.style.top,
                    right: element.style.right
                }));
            }
        };

        // Load saved position
        if (storageKey) {
            const savedPos = localStorage.getItem(storageKey);
            if (savedPos) {
                try {
                    const { top, right } = JSON.parse(savedPos);
                    element.style.bottom = 'auto';
                    element.style.left = 'auto';
                    element.style.top = top;
                    element.style.right = right;
                    
                    // Use ResizeObserver to clamp once it becomes visible or changes size
                    const ro = new ResizeObserver(() => {
                        const elRect = element.getBoundingClientRect();
                        // If element is display:none, it has 0 width/height and we can't reliably clamp its viewport position
                        if (elRect.width === 0 && elRect.height === 0) {
                            return; // Skip clamping until it becomes visible
                        }
                        const currentParentRect = (element.offsetParent as HTMLElement || document.body).getBoundingClientRect();
                        
                        let targetViewportTop = elRect.top;
                        let targetViewportLeft = elRect.left;

                        if (container) {
                            const boundaryRect = container.getBoundingClientRect();
                            targetViewportTop = Math.max(boundaryRect.top + 8, Math.min(boundaryRect.bottom - elRect.height - 8, targetViewportTop));
                            targetViewportLeft = Math.max(boundaryRect.left + 8, Math.min(boundaryRect.right - elRect.width - 8, targetViewportLeft));
                        } else {
                            targetViewportTop = Math.max(8, Math.min(window.innerHeight - elRect.height - 8, targetViewportTop));
                            targetViewportLeft = Math.max(8, Math.min(window.innerWidth - elRect.width - 8, targetViewportLeft));
                        }

                        if (element.id === 'sl-toolbar-panel' && container) {
                            const infoContainer = this.app.container.querySelector('#sl-sheet-info-container') as HTMLElement;
                            if (infoContainer) {
                                const infoRect = infoContainer.getBoundingClientRect();
                                if (targetViewportLeft < (infoRect.right + 8) && targetViewportTop < (infoRect.bottom + 8)) {
                                    targetViewportLeft = infoRect.right + 16;
                                }
                            }
                        }

                        // If it changed, re-apply and save
                        if (Math.abs(targetViewportTop - elRect.top) > 1 || Math.abs(targetViewportLeft - elRect.left) > 1) {
                            element.style.top = `${targetViewportTop - currentParentRect.top}px`;
                            element.style.right = `${currentParentRect.right - (targetViewportLeft + elRect.width)}px`;
                            localStorage.setItem(storageKey, JSON.stringify({
                                top: element.style.top,
                                right: element.style.right
                            }));
                        }
                    });
                    
                    ro.observe(element);
                    if (container) {
                        ro.observe(container);
                    } else {
                        window.addEventListener('resize', () => {
                            if (element.getBoundingClientRect().width > 0) {
                                // Dummy style update to trigger resize or just let resize handle it? 
                                // Better yet, window resize triggers layout, but we can't easily force RO without changing size.
                                // It's fine, element without container uses window bounds, which is rare.
                            }
                        });
                    }
                } catch (e) {
                    console.error(`[EditorManager] Failed to parse saved position for ${element.id}`, e);
                }
            }
        }

        handle.addEventListener('pointerdown', (e) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('button') || target.tagName === 'SELECT' || target.tagName === 'CANVAS' ||
                target.closest('.sl-color-box') || target.closest('.sl-custom-color-box') || target.closest('.sl-cp-palette-slot') || target.closest('.sl-layout-29')) {
                return;
            }

            isDragging = true;
            startX = e.clientX;
            startY = e.clientY;
            initialRect = element.getBoundingClientRect();

            handle.setPointerCapture(e.pointerId);
            element.style.transition = 'none';

            window.addEventListener('pointermove', onPointerMove);
            window.addEventListener('pointerup', stopDrag);
            window.addEventListener('pointercancel', stopDrag);
        });
    }

    switchTool(tool: string) {
        logDev(`[Editor] switchTool: ${tool}`);
        if (tool !== 'picker' && this.magnifier) {
            this.magnifier.style.display = 'none';
        }
        this.app.previousTool = this.app.currentTool;
        // 1. Handle Grid as a non-destructive toggle
        if (tool === 'grid') {
            if (!this.app.toolState.grid.active) {
                this.app.toolState.grid.active = true;
                this.app.toolState.grid.divisions = 4;
            } else if (this.app.toolState.grid.divisions === 4) {
                this.app.toolState.grid.divisions = 8;
            } else if (this.app.toolState.grid.divisions === 8) {
                this.app.toolState.grid.divisions = 16;
            } else {
                this.app.toolState.grid.active = false;
                this.app.toolState.grid.divisions = 4; // Reset to default for next turn
            }

            this.updateFineGrid();

            // Toggle button state without changing currentTool
            const gridBtn = this.app.container.querySelector('[data-tool="grid"]');
            if (this.app.toolState.grid.active) {
                gridBtn?.classList.add('active');
                gridBtn?.setAttribute('title', `${this.app.toolState.grid.divisions}x${this.app.toolState.grid.divisions} Grid (Ctrl+G) - Toggle Grid`);
            } else {
                gridBtn?.classList.remove('active');
                gridBtn?.setAttribute('title', `4x4 Grid (Ctrl+G) - Toggle Grid`);
            }
            return;
        }

        this.app.currentTool = tool as any;

        // Update button states
        const toolButtons = this.app.container.querySelectorAll('.sl-tool-btn');
        toolButtons.forEach((btn: any) => {
            const btnTool = (btn as HTMLElement).dataset.tool;
            if (btnTool === tool) {
                btn.classList.add('active');
            } else if (btnTool !== 'grid') { // Don't touch grid's active state
                btn.classList.remove('active');
            }
        });

        // Update cursor
        this.updateCursor();

        // Update settings panels visibility
        const brushSettings = this.app.container.querySelector('#sl-brush-settings') as HTMLElement;
        const colorConfig = this.app.container.querySelector('#sl-color-config') as HTMLElement;
        const sizeConfig = this.app.container.querySelector('#sl-size-config') as HTMLElement;
        const bucketConfig = this.app.container.querySelector('#sl-bucket-config') as HTMLElement;

        const isBrushOrEraser = tool === 'brush' || tool === 'eraser';
        const isBrush = tool === 'brush' || tool === 'bucket';
        const isBucket = tool === 'bucket';
        const isBrushOrEraserOrBucket = tool === 'brush' || tool === 'eraser' || tool === 'bucket';

        if (brushSettings) brushSettings.style.display = isBrushOrEraserOrBucket ? 'flex' : 'none';
        if (colorConfig) colorConfig.style.display = isBrush ? 'grid' : 'none';
        if (sizeConfig) sizeConfig.style.display = isBrushOrEraser ? 'block' : 'none';
        if (bucketConfig) bucketConfig.style.display = isBucket ? 'block' : 'none';

    }

    updateCursor() {
        const canvas = this.app.container.querySelector('#sl-sheet-canvas') as HTMLCanvasElement;
        if (!canvas) return;

        const cursorMap: Record<string, string> = {
            'mouse': 'grab',
            'brush': 'crosshair',
            'eraser': 'cell',
            'bucket': 'crosshair',
            'grid': 'default',
            'scale': 'nwse-resize',
            'rotate': 'alias',
            'transpose': 'ns-resize'
        };

        canvas.style.cursor = cursorMap[this.app.currentTool] || 'default';
    }

    updateFineGrid() {
        const svg = this.app.container.querySelector('#sl-sheet-grid') as SVGSVGElement | null;
        if (!svg) return;

        // Clear existing fine grid elements (identified by class)
        const existing = svg.querySelectorAll('.sl-fine-grid-line');
        existing.forEach(el => el.remove());

        if (!this.app.toolState.grid.active) return;

        const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
        const frameW = parseInt(wInput?.value || '32');
        const frameH = parseInt(hInput?.value || '32');

        const canvas = this.app.container.querySelector('#sl-sheet-canvas') as HTMLCanvasElement;
        if (!canvas) return;

        const texW = canvas.width;
        const texH = canvas.height;

        const div = this.app.toolState.grid.divisions || 4;
        const subW = frameW / div;
        const subH = frameH / div;

        if (subW < 1 || subH < 1) return; // Prevent too dense grid

        // Vertical fine lines
        let x = subW;
        while (x < texW) {
            // Skip if it overlaps with a main frame line (every 4th line)
            if (x % frameW !== 0) {
                const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
                const posPct = (x / texW) * 100;

                line.setAttribute('x1', `${posPct}%`);
                line.setAttribute('y1', '0');
                line.setAttribute('x2', `${posPct}%`);
                line.setAttribute('y2', '100%');
                line.setAttribute('stroke', '#3c3c3c'); // Dark slate/Gray-600
                line.style.strokeWidth = 'calc(2px * var(--sl-inverse-scale, 1))'; // Increased from 1px
                line.setAttribute('stroke-opacity', '0.6'); // Slightly more opaque
                line.setAttribute('class', 'sl-fine-grid-line');
                line.style.pointerEvents = 'none';

                svg.insertBefore(line, svg.firstChild);
            }
            x += subW;
        }

        // Horizontal fine lines
        let y = subH;
        while (y < texH) {
            if (y % frameH !== 0) {
                const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
                const posPct = (y / texH) * 100;

                line.setAttribute('x1', '0');
                line.setAttribute('y1', `${posPct}%`);
                line.setAttribute('x2', '100%');
                line.setAttribute('y2', `${posPct}%`);
                line.setAttribute('stroke', '#3c3c3c');
                line.style.strokeWidth = 'calc(2px * var(--sl-inverse-scale, 1))';
                line.setAttribute('stroke-opacity', '0.6');
                line.setAttribute('class', 'sl-fine-grid-line');
                line.style.pointerEvents = 'none';

                svg.insertBefore(line, svg.firstChild);
            }
            y += subH;
        }
    }

        applyScale(targetW: number, targetH: number) {
        if (!this.app.dataCanvas || !this.app.currentBase64) return;
        if (targetW <= 0 || targetH <= 0) return;

        const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
        const oldFrameW = parseInt(wInput?.value || '32');
        const oldFrameH = parseInt(hInput?.value || '32');

        const oldW = this.app.dataCanvas.width;
        const oldH = this.app.dataCanvas.height;

        // Calculate Ratios based on frame scaling
        const ratioX = targetW / oldFrameW;
        const ratioY = targetH / oldFrameH;

        const newSheetW = Math.max(1, Math.round(oldW * ratioX));
        const newSheetH = Math.max(1, Math.round(oldH * ratioY));

        const temp = document.createElement('canvas');
        temp.width = newSheetW;
        temp.height = newSheetH;
        const ctx = temp.getContext('2d') as CanvasRenderingContext2D | null;
        if (!ctx) return;

        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(this.app.dataCanvas, 0, 0, newSheetW, newSheetH);

        this.app.dataCanvas.width = newSheetW;
        this.app.dataCanvas.height = newSheetH;
        const dCtx = this.app.dataCanvas.getContext('2d') as CanvasRenderingContext2D | null;
        if (dCtx) {
            dCtx.imageSmoothingEnabled = false;
            dCtx.drawImage(temp, 0, 0);
        }

        // Update Configuration Metadata (Frame Size)
        if (wInput) wInput.value = targetW.toString();
        if (hInput) hInput.value = targetH.toString();
        
        const scaleWInput = this.app.container.querySelector('#sl-scale-width') as HTMLInputElement;
        const scaleHInput = this.app.container.querySelector('#sl-scale-height') as HTMLInputElement;
        if (scaleWInput) scaleWInput.value = targetW.toString();
        if (scaleHInput) scaleHInput.value = targetH.toString();

        this.app.currentBase64 = this.app.dataCanvas.toDataURL();
        this.app.updatePreview(this.app.currentBase64, false); // Preserve view, but update default zoom
        logDev('[Editor] applyScale committed');
        this.app.historyManager.takeSnapshot();
    }

    applyFlip(direction: 'horizontal' | 'vertical') {
        if (this.app.pastedData) {
            const pd = this.app.pastedData;
            const sourceImg = pd.img;
            const canvas = document.createElement('canvas');
            canvas.width = sourceImg.width;
            canvas.height = sourceImg.height;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                if (direction === 'horizontal') {
                    ctx.translate(canvas.width, 0);
                    ctx.scale(-1, 1);
                } else {
                    ctx.translate(0, canvas.height);
                    ctx.scale(1, -1);
                }
                ctx.drawImage(sourceImg, 0, 0);
                pd.img = canvas;
                this.updatePasteOverlay();
                this.app.historyManager.takeSnapshot();
            }
            return;
        }

        if (!this.app.dataCanvas || !this.app.currentBase64) return;

        // Get Frame Dimensions
        const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
        const fw = parseInt(wInput?.value || '32');
        const fh = parseInt(hInput?.value || '32');

        const ctx = this.app.dataCanvas.getContext('2d');
        if (!ctx) return;

        const cols = Math.ceil(this.app.dataCanvas.width / fw);
        const rows = Math.ceil(this.app.dataCanvas.height / fh);

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = fw;
        tempCanvas.height = fh;
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) return;

        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                if (this.app.selectedFrame && (this.app.selectedFrame.col !== c || this.app.selectedFrame.row !== r)) continue;

                const x = c * fw;
                const y = r * fh;

                // Extract Frame
                const frameData = ctx.getImageData(x, y, fw, fh);

                // Clear Temp & Draw Frame
                tempCtx.clearRect(0, 0, fw, fh);
                tempCtx.putImageData(frameData, 0, 0);

                // Clear Original Frame Area (prevent ghosting)
                ctx.clearRect(x, y, fw, fh);

                // Flip Frame
                ctx.save();
                ctx.translate(x, y); // Move to cell origin

                if (direction === 'horizontal') {
                    ctx.translate(fw, 0);
                    ctx.scale(-1, 1);
                } else {
                    ctx.translate(0, fh);
                    ctx.scale(1, -1);
                }

                ctx.drawImage(tempCanvas, 0, 0);
                ctx.restore();
            }
        }

        this.app.currentBase64 = this.app.dataCanvas.toDataURL();
        this.app.updatePreview(this.app.currentBase64, false);
        this.app.historyManager.takeSnapshot();
        this.app.checkDirty();
        this.app.updateButtonStates();
    }

    applyRotate() {
        if (this.app.pastedData) {
            const pd = this.app.pastedData;
            const sourceImg = pd.img;
            const canvas = document.createElement('canvas');
            canvas.width = sourceImg.height;
            canvas.height = sourceImg.width;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.translate(canvas.width / 2, canvas.height / 2);
                ctx.rotate(Math.PI / 2);
                ctx.drawImage(sourceImg, -sourceImg.width / 2, -sourceImg.height / 2);
                
                pd.img = canvas;
                
                const cx = pd.x + pd.w / 2;
                const cy = pd.y + pd.h / 2;
                const newW = pd.h;
                const newH = pd.w;
                pd.w = newW;
                pd.h = newH;
                pd.x = cx - newW / 2;
                pd.y = cy - newH / 2;

                this.updatePasteOverlay();
                this.app.historyManager.takeSnapshot();
            }
            return;
        }

        if (!this.app.dataCanvas || !this.app.currentBase64) return;

        const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;

        const fw = parseInt(wInput?.value || '32');
        const fh = parseInt(hInput?.value || '32');

        const cols = Math.ceil(this.app.dataCanvas.width / fw);
        const rows = Math.ceil(this.app.dataCanvas.height / fh);

        // New Frame Dimensions (Swapped)
        const newFw = fh;
        const newFh = fw;

        // New Canvas Dimensions (Cols * NewW, Rows * NewH) -> Keeps Strip Integration
        const newW = cols * newFw;
        const newH = rows * newFh;

        const newCanvas = document.createElement('canvas');
        newCanvas.width = newW;
        newCanvas.height = newH;
        const newCtx = newCanvas.getContext('2d');
        if (!newCtx) return;
        newCtx.imageSmoothingEnabled = false;

        const oldCtx = this.app.dataCanvas.getContext('2d');
        if (!oldCtx) return;

        const tempFrame = document.createElement('canvas');
        tempFrame.width = fw;
        tempFrame.height = fh;
        const frameCtx = tempFrame.getContext('2d');
        if (!frameCtx) return;

        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                if (this.app.selectedFrame && (this.app.selectedFrame.col !== c || this.app.selectedFrame.row !== r)) {
                    // If not selected, copy without rotation
                    const sx = c * fw;
                    const sy = r * fh;
                    const dx = c * newFw;
                    const dy = r * newFh;
                    const frameData = oldCtx.getImageData(sx, sy, fw, fh);
                    frameCtx.clearRect(0, 0, fw, fh);
                    frameCtx.putImageData(frameData, 0, 0);
                    newCtx.drawImage(tempFrame, dx, dy);
                    continue;
                }

                // Source Coordinates
                const sx = c * fw;
                const sy = r * fh;

                // Dest Coordinates
                const dx = c * newFw;
                const dy = r * newFh;

                // Extract Source Frame
                const frameData = oldCtx.getImageData(sx, sy, fw, fh);
                frameCtx.clearRect(0, 0, fw, fh);
                frameCtx.putImageData(frameData, 0, 0);

                // Rotate and Draw into New Canvas
                newCtx.save();
                // Move to center of DESTINATION cell
                newCtx.translate(dx + newFw / 2, dy + newFh / 2);
                newCtx.rotate(Math.PI / 2);
                // Draw SOURCE frame centered at origin
                newCtx.drawImage(tempFrame, -fw / 2, -fh / 2);
                newCtx.restore();
            }
        }

        // Apply to Data Canvas
        this.app.dataCanvas.width = newW;
        this.app.dataCanvas.height = newH;
        const dCtx = this.app.dataCanvas.getContext('2d');
        if (dCtx) {
            dCtx.imageSmoothingEnabled = false;
            dCtx.drawImage(newCanvas, 0, 0);
        }

        // SWAP Metadata Inputs
        if (wInput) wInput.value = newFw.toString();
        if (hInput) hInput.value = newFh.toString();

        this.app.currentBase64 = this.app.dataCanvas.toDataURL();
        this.app.updatePreview(this.app.currentBase64, false);
        this.app.historyManager.takeSnapshot();
        this.app.updateButtonStates();
    }

    applyTranspose() {
        const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
        const fInput = this.app.container.querySelector('#sl-meta-frames') as HTMLInputElement;

        if (!wInput || !hInput || !fInput) return;

        const fw = parseInt(wInput.value) || 32;
        const fh = parseInt(hInput.value) || 32;
        const count = parseInt(fInput.value) || 1;
        
        // Current orientation BEFORE transpose
        // Deduced from canvas dimensions
        const isHorz = this.app.dataCanvas.width >= this.app.dataCanvas.height;

        // 1. Extract Frames
        const frames: ImageData[] = [];
        const ctx = this.app.dataCanvas.getContext('2d');
        if (!ctx) return;

        for (let i = 0; i < count; i++) {
            const x = isHorz ? i * fw : 0;
            const y = isHorz ? 0 : i * fh;
            // Guard against out of bounds reading
            if (x + fw <= this.app.dataCanvas.width && y + fh <= this.app.dataCanvas.height) {
                frames.push(ctx.getImageData(x, y, fw, fh));
            } else {
                // If the canvas is smaller than expected, push empty or partial?
                // Better to just stop or push a blank frame to preserve index
                frames.push(ctx.createImageData(fw, fh));
            }
        }

        // 2. Prep New Layout
        // New orientation will be opposite
        const newIsHorz = !isHorz;
        const newTotalW = newIsHorz ? count * fw : fw;
        const newTotalH = newIsHorz ? fh : count * fh;

        // 3. Resize Canvas
        this.app.dataCanvas.width = newTotalW;
        this.app.dataCanvas.height = newTotalH;

        // 4. Draw Frames in New Order
        frames.forEach((frame, i) => {
            const nx = newIsHorz ? i * fw : 0;
            const ny = newIsHorz ? 0 : i * fh;
            ctx.putImageData(frame, nx, ny);
        });

        // 6. Update Selection (Follow the frame)
        if (this.app.selectedFrame) {
            const oldCol = this.app.selectedFrame.col;
            this.app.selectedFrame.col = this.app.selectedFrame.row;
            this.app.selectedFrame.row = oldCol;
        }

        // 7. Update Preview
        this.app.currentBase64 = this.app.dataCanvas.toDataURL();
        this.app.updatePreview(this.app.currentBase64, false);
        this.app.updateButtonStates();
        
        // 8. Update Orientation Select UI
        const orientationSelect = this.app.container.querySelector('#sl-meta-orientation') as HTMLSelectElement;
        if (orientationSelect) {
            orientationSelect.value = newIsHorz ? 'horizontal' : 'vertical';
        }
    }



    refreshViewCanvas(viewCanvas: HTMLCanvasElement, dataCanvas: HTMLCanvasElement) {
        const ctx = viewCanvas.getContext('2d');
        if (!ctx) return;

        ctx.globalAlpha = 1.0;
        ctx.clearRect(0, 0, viewCanvas.width, viewCanvas.height);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(dataCanvas, 0, 0);

        // Reapply darkening overlay
        const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
        const fInput = this.app.container.querySelector('#sl-meta-frames') as HTMLInputElement;

        const frameW = parseInt(wInput?.value || '32');
        const frameH = parseInt(hInput?.value || '32');
        const frameCount = parseInt(fInput?.value || '3');
        
        const isHorz = dataCanvas.width >= dataCanvas.height;

        const targetW = isHorz ? frameW * frameCount : frameW;
        const targetH = isHorz ? frameH : frameH * frameCount;

        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        if (viewCanvas.width > targetW) {
            ctx.fillRect(targetW, 0, viewCanvas.width - targetW, viewCanvas.height);
        }
        if (viewCanvas.height > targetH) {
            ctx.fillRect(0, targetH, targetW, viewCanvas.height - targetH);
        }
    }

    applyChangesAndRefresh(dataCanvas: HTMLCanvasElement, fw: number, fh: number, targetW: number, targetH: number) {
        // Adaptive Resize: Cleanup empty rows/cols outside the working area
        const dims = this.getTightDimensions(dataCanvas, fw, fh, targetW, targetH);

        if (dims.w !== dataCanvas.width || dims.h !== dataCanvas.height) {
            const temp = document.createElement('canvas');
            temp.width = dataCanvas.width;
            temp.height = dataCanvas.height;
            temp.getContext('2d', { alpha: true })!.drawImage(dataCanvas, 0, 0);

            dataCanvas.width = dims.w;
            dataCanvas.height = dims.h;
            dataCanvas.getContext('2d', { alpha: true })!.drawImage(temp, 0, 0);
        }

        // Refresh Preview
        this.app.currentBase64 = dataCanvas.toDataURL(); // Changed from canvas.toDataURL() to dataCanvas.toDataURL()
        this.app.updatePreview(this.app.currentBase64, false);
        // The original instruction's logDev call was for applyFlip, which is not in this method.
        // Assuming the intent was to add a log for this method, or that applyFlip is elsewhere.
        // For now, I'll add a generic log for applyChangesAndRefresh.
        // If applyFlip is added later, this log can be adjusted.
        // logDev(`[Editor] applyFlip (${direction}) committed`); // This line was in the instruction but doesn't fit here.
        logDev(`[Editor] applyChangesAndRefresh committed`); // Added logDev for applyChangesAndRefresh
        this.app.historyManager.takeSnapshot();
    }

    private getTightDimensions(canvas: HTMLCanvasElement, fw: number, fh: number, minW: number, minH: number) {
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        const cols = Math.ceil(canvas.width / fw);
        const rows = Math.ceil(canvas.height / fh);

        let maxCol = Math.ceil(minW / fw) - 1;
        let maxRow = Math.ceil(minH / fh) - 1;

        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                // If this cell is outside our current "tight" bounds, check if it has data
                if (c > maxCol || r > maxRow) {
                    const pixels = ctx.getImageData(c * fw, r * fh, fw, fh).data;
                    let hasData = false;
                    for (let i = 3; i < pixels.length; i += 4) {
                        if (pixels[i] > 0) {
                            hasData = true;
                            break;
                        }
                    }
                    if (hasData) {
                        maxCol = Math.max(maxCol, c);
                        maxRow = Math.max(maxRow, r);
                    }
                }
            }
        }

        return {
            w: (maxCol + 1) * fw,
            h: (maxRow + 1) * fh
        };
    }

    updateSelectionHighlight(fw: number, fh: number) {
        const svg = this.app.container.querySelector('#sl-sheet-grid');
        if (!svg) return;

        let outer = document.getElementById('sl-selection-highlight') as SVGRectElement | null;
        let inner = document.getElementById('sl-selection-highlight-inner') as SVGRectElement | null;

        if (!this.app.selectedFrame || fw === 0 || fh === 0) {
            if (outer) outer.style.display = 'none';
            if (inner) inner.style.display = 'none';
            return;
        }

        if (!outer) {
            outer = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            outer.id = 'sl-selection-highlight';
            outer.setAttribute('fill', 'rgba(99, 102, 241, 0.1)'); // Subtle blue tint
            outer.setAttribute('stroke', 'transparent');
            outer.setAttribute('stroke-width', '8');
            outer.setAttribute('vector-effect', 'non-scaling-stroke');
            outer.style.pointerEvents = 'none';
            svg.appendChild(outer);
        }

        if (!inner) {
            inner = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            inner.id = 'sl-selection-highlight-inner';
            inner.setAttribute('fill', 'none');
            inner.setAttribute('stroke', '#0078d4'); // Primary Blue
            inner.setAttribute('stroke-width', '6');
            inner.setAttribute('stroke-dasharray', '16, 16');
            inner.setAttribute('vector-effect', 'non-scaling-stroke');
            inner.style.pointerEvents = 'none';

            if (!document.getElementById('sl-svg-selection-anim')) {
                const s = document.createElement('style');
                s.id = 'sl-svg-selection-anim';
                s.textContent = `
                    #sl-selection-highlight-inner {
                        animation: sl-svg-march 0.5s linear infinite;
                    }
                `;
                document.head.appendChild(s);
            }
            svg.appendChild(inner);
        }

        let x = 0;
        let y = 0;
        let pWidth = 0;
        let pHeight = 0;

        if (this.app.selectedFrame.free && this.app.selectedFrame.px !== undefined) {
            x = this.app.selectedFrame.px;
            y = this.app.selectedFrame.py!;
            pWidth = this.app.selectedFrame.pw!;
            pHeight = this.app.selectedFrame.ph!;
        } else {
            x = this.app.selectedFrame.col * fw;
            y = this.app.selectedFrame.row * fh;
            pWidth = fw;
            pHeight = fh;
        }

        const scale = this.app?.transformState?.scale || 1;
        const inset = 3.5 / scale;

        outer.setAttribute('x', `${x + inset}`);
        outer.setAttribute('y', `${y + inset}`);
        outer.setAttribute('width', `${pWidth - inset * 2}`);
        outer.setAttribute('height', `${pHeight - inset * 2}`);
        outer.style.display = 'block';

        inner.setAttribute('x', `${x + inset}`);
        inner.setAttribute('y', `${y + inset}`);
        inner.setAttribute('width', `${pWidth - inset * 2}`);
        inner.setAttribute('height', `${pHeight - inset * 2}`);
        inner.style.display = 'block';
    }

    updatePasteOverlay() {
        const svg = this.app.container.querySelector('#sl-sheet-grid');
        if (!svg) return;

        let pasteInner = document.getElementById('sl-paste-highlight-inner') as SVGRectElement | null;
        let pasteImage = document.getElementById('sl-paste-image') as SVGImageElement | null;

        const handleIds = ['nw', 'ne', 'sw', 'se'];
        const handles: Record<string, SVGRectElement | null> = {};
        handleIds.forEach(id => {
            handles[id] = document.getElementById(`sl-paste-handle-${id}`) as SVGRectElement | null;
        });
        let rotateHandle = document.getElementById('sl-paste-handle-rotate') as SVGCircleElement | null;

        if (!this.app.pastedData) {
            if (pasteInner) pasteInner.style.display = 'none';
            if (pasteImage) pasteImage.style.display = 'none';
            if (rotateHandle) rotateHandle.style.display = 'none';
            handleIds.forEach(id => { if (handles[id]) handles[id]!.style.display = 'none'; });
            document.getElementById('sl-paste-highlight')?.remove(); // Remove old solid box if exists
            document.getElementById('sl-paste-scale-handle')?.remove(); // Remove old single handle
            return;
        }

        if (!pasteImage) {
            pasteImage = document.createElementNS('http://www.w3.org/2000/svg', 'image');
            pasteImage.id = 'sl-paste-image';
            pasteImage.setAttribute('preserveAspectRatio', 'none');
            pasteImage.style.imageRendering = 'pixelated';
            pasteImage.style.pointerEvents = 'none';
            svg.appendChild(pasteImage);
        }
        pasteImage.setAttributeNS('http://www.w3.org/1999/xlink', 'href', (this.app.pastedData.img as any).src || (this.app.pastedData.img as HTMLCanvasElement).toDataURL());
        pasteImage.setAttribute('x', `${this.app.pastedData.x}`);
        pasteImage.setAttribute('y', `${this.app.pastedData.y}`);
        pasteImage.setAttribute('width', `${this.app.pastedData.w}`);
        pasteImage.setAttribute('height', `${this.app.pastedData.h}`);

        const deg = (this.app.pastedData.rotation * 180) / Math.PI;
        const cx = this.app.pastedData.x + this.app.pastedData.w / 2;
        const cy = this.app.pastedData.y + this.app.pastedData.h / 2;
        const tScale = (this.app.pastedData as any).trashScale || 1.0;
        
        pasteImage.setAttribute('transform', `translate(${cx} ${cy}) scale(${tScale}) rotate(${deg}) translate(${-cx} ${-cy})`);

        pasteImage.style.display = 'block';

        if (tScale < 1.0) {
            if (pasteInner) pasteInner.style.display = 'none';
            if (rotateHandle) rotateHandle.style.display = 'none';
            handleIds.forEach(id => { if (handles[id]) handles[id]!.style.display = 'none'; });
            return;
        }

        if (!pasteInner) {
            pasteInner = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            pasteInner.id = 'sl-paste-highlight-inner';
            pasteInner.setAttribute('fill', 'none');
            pasteInner.setAttribute('stroke', '#000');
            pasteInner.setAttribute('stroke-width', '6');
            pasteInner.setAttribute('stroke-dasharray', '16, 16');
            pasteInner.setAttribute('vector-effect', 'non-scaling-stroke');
            pasteInner.style.pointerEvents = 'none';

            if (!document.getElementById('sl-svg-paste-anim')) {
                const s = document.createElement('style');
                s.id = 'sl-svg-paste-anim';
                s.textContent = `
                    #sl-paste-highlight-inner {
                        animation: sl-svg-march-paste 0.5s linear infinite;
                    }
                    @keyframes sl-svg-march-paste {
                        to { stroke-dashoffset: 32; }
                    }
                `;
                document.head.appendChild(s);
            }
            svg.appendChild(pasteInner);
        }

        const { x, y, w, h } = this.app.pastedData;
        pasteInner.setAttribute('x', `${x}`);
        pasteInner.setAttribute('y', `${y}`);
        pasteInner.setAttribute('width', `${w}`);
        pasteInner.setAttribute('height', `${h}`);
        pasteInner.setAttribute('transform', `rotate(${deg} ${cx} ${cy})`);
        pasteInner.style.display = 'block';

        // Add 4 white squares in corners
        const hSize = 16;
        const hHalf = hSize / 2;
        const rotCos = Math.cos(this.app.pastedData.rotation);
        const rotSin = Math.sin(this.app.pastedData.rotation);

        handleIds.forEach(id => {
            if (!handles[id]) {
                const hNode = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                hNode.id = `sl-paste-handle-${id}`;
                hNode.setAttribute('fill', '#fff');
                hNode.setAttribute('stroke', '#000');
                hNode.setAttribute('stroke-width', '1.5');
                hNode.setAttribute('width', `${hSize}`);
                hNode.setAttribute('height', `${hSize}`);
                hNode.setAttribute('vector-effect', 'non-scaling-stroke');
                hNode.style.pointerEvents = 'none'; // Clicks handled by setupSpriteEditor scale logic
                hNode.style.transformBox = 'fill-box';
                hNode.style.transformOrigin = 'center';
                svg.appendChild(hNode);
                handles[id] = hNode;
            }

            let ox = x;
            let oy = y;
            if (id.includes('e')) ox = x + w;
            if (id.includes('s')) oy = y + h;

            const rx = cx + (ox - cx) * rotCos - (oy - cy) * rotSin;
            const ry = cy + (ox - cx) * rotSin + (oy - cy) * rotCos;

            const hx = rx - hHalf;
            const hy = ry - hHalf;

            handles[id]!.setAttribute('x', `${hx}`);
            handles[id]!.setAttribute('y', `${hy}`);
            handles[id]!.style.transform = `rotate(${deg}deg) scale(var(--sl-inverse-scale))`;
            handles[id]!.style.display = 'block';
        });

        // Update Rotation Handle
        if (!rotateHandle) {
            rotateHandle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            rotateHandle.id = 'sl-paste-handle-rotate';
            rotateHandle.setAttribute('r', '8');
            rotateHandle.setAttribute('fill', '#fff'); // White as requested
            rotateHandle.setAttribute('stroke', '#000');
            rotateHandle.setAttribute('stroke-width', '1.5');
            rotateHandle.setAttribute('vector-effect', 'non-scaling-stroke');
            rotateHandle.style.cursor = 'crosshair';
            rotateHandle.style.pointerEvents = 'none';
            rotateHandle.style.transformBox = 'fill-box';
            rotateHandle.style.transformOrigin = 'center';
            rotateHandle.style.transform = 'scale(var(--sl-inverse-scale))';
            svg.appendChild(rotateHandle);
        }

        const hOffset = -4;
        const rhX = cx + (cx - cx) * Math.cos(this.app.pastedData.rotation) - (y + hOffset - cy) * Math.sin(this.app.pastedData.rotation);
        const rhY = cy + (cx - cx) * Math.sin(this.app.pastedData.rotation) + (y + hOffset - cy) * Math.cos(this.app.pastedData.rotation);
        rotateHandle.setAttribute('cx', `${rhX}`);
        rotateHandle.setAttribute('cy', `${rhY}`);
        rotateHandle.style.display = 'block';
    }

    renderToolPreview(sheetX: number, sheetY: number, viewCanvas: HTMLCanvasElement, dataCanvas: HTMLCanvasElement) {
        const vCtx = viewCanvas.getContext('2d');
        if (!vCtx) return;

        // Reset to clean data state
        this.refreshViewCanvas(viewCanvas, dataCanvas);

        if (this.app.currentTool === 'mouse' || this.app.currentTool === 'grid' || this.app.currentTool === 'bucket') return;

        const tool = this.app.currentTool as 'brush' | 'eraser';
        const size = this.app.toolState[tool]?.size || 1;
        const tx = Math.floor(sheetX);
        const ty = Math.floor(sheetY);
        const fx = Math.floor(tx - (size - 1) / 2);
        const fy = Math.floor(ty - (size - 1) / 2);

        vCtx.save();

        if (tool === 'eraser' || this.app.toolState.brush.mode === 'transparent') {
            vCtx.globalAlpha = 0.5;
            vCtx.fillStyle = 'rgba(255,255,255,1)';
        } else {
            vCtx.globalAlpha = 1.0;
            vCtx.fillStyle = this.app.toolState.brush.color;
        }

        if (tool === 'eraser' || size <= 2) {
            vCtx.fillRect(fx, fy, size, size);
        } else {
            const radius = (size - 1) / 2;
            const r2 = (size / 2) * (size / 2);
            for (let ix = 0; ix < size; ix++) {
                for (let iy = 0; iy < size; iy++) {
                    const dx = ix - radius;
                    const dy = iy - radius;
                    if (dx * dx + dy * dy < r2) {
                        vCtx.fillRect(fx + ix, fy + iy, 1, 1);
                    }
                }
            }
        }
        vCtx.restore();
    }


    getRotatedCursor(handle: 'nw' | 'ne' | 'sw' | 'se', rotation: number): string {
        // Map handle to standard CSS cursor and apply rotation
        const cursors: Record<string, number> = { 'nw': 0, 'ne': 90, 'se': 180, 'sw': 270 };
        const baseAngle = cursors[handle];
        const totalDeg = (baseAngle + (rotation * 180 / Math.PI)) % 360;

        // Choose the closest standard cursor
        if (totalDeg < 22.5 || totalDeg >= 337.5) return 'nwse-resize';
        if (totalDeg < 67.5) return 'ns-resize';
        if (totalDeg < 112.5) return 'nesw-resize';
        if (totalDeg < 157.5) return 'ew-resize';
        if (totalDeg < 202.5) return 'nwse-resize';
        if (totalDeg < 247.5) return 'ns-resize';
        if (totalDeg < 292.5) return 'nesw-resize';
        return 'ew-resize';
    }

    setupSpriteEditor(viewCanvas: HTMLCanvasElement, dataCanvas: HTMLCanvasElement, fw: number, fh: number, targetW: number, targetH: number) {
        viewCanvas.style.touchAction = 'none';
        viewCanvas.oncontextmenu = (e) => e.preventDefault();

        viewCanvas.onpointerdown = (e) => {
            if (this.app.isLoading) return;
            if (e.button !== 0 && e.button !== 2) return;

            // Immediately reflect the active sprite in the Explorer
            this.app.renderExplorer();

            // Right-click drag/pan logic for mouse tool
            if (e.button === 2 && this.app.currentTool === 'mouse') {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                viewCanvas.setPointerCapture(e.pointerId);

                let isDragging = true;
                const dragStartTime = Date.now();
                const dragStartX = e.clientX;
                const dragStartY = e.clientY;
                const startX = e.clientX - this.app.transformState.pX;
                const startY = e.clientY - this.app.transformState.pY;
                viewCanvas.style.cursor = 'grabbing';

                const move = (me: PointerEvent) => {
                    if (isDragging) {
                        this.app.transformState.pX = me.clientX - startX;
                        this.app.transformState.pY = me.clientY - startY;
                        if (this.app.updateSheetTransform) this.app.updateSheetTransform();
                    }
                };

                const up = (ue: PointerEvent) => {
                    window.removeEventListener('pointermove', move);
                    window.removeEventListener('pointerup', up);
                    window.removeEventListener('pointercancel', up);
                    viewCanvas.releasePointerCapture(ue.pointerId);
                    viewCanvas.style.cursor = 'default';
                    isDragging = false;

                    const duration = Date.now() - dragStartTime;
                    const dist = Math.hypot(ue.clientX - dragStartX, ue.clientY - dragStartY);
                    
                    // Reset zoom/pan if it was a quick click with minimal movement
                    if (duration < 300 && dist < 10) {
                        this.app.transformState.scale = this.app.defaultScale;
                        this.app.transformState.pX = 0;
                        this.app.transformState.pY = 0;
                        if (this.app.updateSheetTransform) this.app.updateSheetTransform();
                    }
                };

                window.addEventListener('pointermove', move);
                window.addEventListener('pointerup', up);
                window.addEventListener('pointercancel', up);
                return;
            }

            // Stop native swiping/scrolling
            e.preventDefault();

            if (!e.isPrimary && e.pointerType === 'touch') {
                window.dispatchEvent(new Event('sl-cancel-tool'));
                return; // Let the container handle the multi-touch pan/zoom
            }

            viewCanvas.setPointerCapture(e.pointerId);
            const rect = viewCanvas.getBoundingClientRect();

            const clientX = e.clientX - rect.left;
            const clientY = e.clientY - rect.top;

            const displayScale = viewCanvas.width / rect.width; // CSS to Internal Pixel ratio
            const rawX = clientX * displayScale;
            const rawY = clientY * displayScale;

            const x = rawX;
            const y = rawY;

            if (x >= dataCanvas.width || y >= dataCanvas.height) return;

            // Handle Pasted Data Interaction
            if (this.app.pastedData) {
                const { x: px, y: py, w: pw, h: ph, rotation: rot } = this.app.pastedData;
                const cx = px + pw / 2;
                const cy = py + ph / 2;
                const cos = Math.cos(-rot);
                const sin = Math.sin(-rot);
                const lx = cx + (x - cx) * cos - (y - cy) * sin;
                const ly = cy + (x - cx) * sin + (y - cy) * cos;

                const handleSize = 14 * displayScale;
                const offset = -4;

                // ROTATION HANDLE (Local Check)
                if (Math.abs(lx - cx) < handleSize && Math.abs(ly - (py + offset)) < handleSize) {
                    this.app.pastedData.activeHandle = 'rotate';
                }
                // NW
                else if (Math.abs(lx - px) < handleSize && Math.abs(ly - py) < handleSize) {
                    this.app.pastedData.activeHandle = 'nw';
                }
                // NE
                else if (Math.abs(lx - (px + pw)) < handleSize && Math.abs(ly - py) < handleSize) {
                    this.app.pastedData.activeHandle = 'ne';
                }
                // SW
                else if (Math.abs(lx - px) < handleSize && Math.abs(ly - (py + ph)) < handleSize) {
                    this.app.pastedData.activeHandle = 'sw';
                }
                // SE
                else if (Math.abs(lx - (px + pw)) < handleSize && Math.abs(ly - (py + ph)) < handleSize) {
                    this.app.pastedData.activeHandle = 'se';
                }
                // MOVE
                else if (lx >= px && lx <= px + pw && ly >= py && ly <= py + ph) {
                    this.app.pastedData.activeHandle = 'move';
                }

                if (this.app.pastedData.activeHandle) {
                    this.app.pastedData.startX = x;
                    this.app.pastedData.startY = y;
                    this.app.pastedData.startLX = lx; // Local X
                    this.app.pastedData.startLY = ly; // Local Y
                    this.app.pastedData.startW = pw;
                    this.app.pastedData.startH = ph;
                    this.app.pastedData.startPosX = px;
                    this.app.pastedData.startPosY = py;
                    this.app.pastedData.startRotation = rot;

                    // Calculate Global Anchor
                    let alx = px, aly = py; // Default Anchor
                    if (this.app.pastedData.activeHandle === 'nw') { alx = px + pw; aly = py + ph; }
                    else if (this.app.pastedData.activeHandle === 'ne') { alx = px; aly = py + ph; }
                    else if (this.app.pastedData.activeHandle === 'sw') { alx = px + pw; aly = py; }
                    else if (this.app.pastedData.activeHandle === 'se') { alx = px; aly = py; }

                    const cosR = Math.cos(rot);
                    const sinR = Math.sin(rot);
                    this.app.pastedData.startAncX = cx + (alx - cx) * cosR - (aly - cy) * sinR;
                    this.app.pastedData.startAncY = cy + (alx - cx) * sinR + (aly - cy) * cosR;

                    e.stopImmediatePropagation();
                    return;
                } else {
                    // Clicking outside commits
                    this.app.clipboardManager.commitPaste();
                }
            }

            // Route to appropriate tool handler
            switch (this.app.currentTool) {
                case 'mouse':
                    this.handleMouseTool(e, viewCanvas, dataCanvas, fw, fh, targetW, targetH, x, y);
                    break;
                case 'brush':
                    this.handleBrushTool(e, viewCanvas, dataCanvas, x, y);
                    break;
                case 'eraser':
                    this.handleEraserTool(e, viewCanvas, dataCanvas, x, y);
                    break;
                case 'bucket':
                    this.handleBucketTool(e, viewCanvas, dataCanvas, x, y);
                    break;
                case 'picker':
                    this.handlePickerTool(e, viewCanvas, dataCanvas, x, y);
                    break;
            }
        };

        // VISUAL CURSOR PREVIEW
        viewCanvas.onpointermove = (e) => {
            e.preventDefault();
            const rect = viewCanvas.getBoundingClientRect();
            const displayScale = viewCanvas.width / rect.width;
            const x = (e.clientX - rect.left) * displayScale;
            const y = (e.clientY - rect.top) * displayScale;

            if (this.app.pastedData) {
                const { x: px, y: py, w: pw, h: ph, rotation: rot } = this.app.pastedData;
                let cursor = 'default';

                const cx = px + pw / 2;
                const cy = py + ph / 2;
                const cos = Math.cos(-rot);
                const sin = Math.sin(-rot);
                const lx = cx + (x - cx) * cos - (y - cy) * sin;
                const ly = cy + (x - cx) * sin + (y - cy) * cos;
                const offset = -4;
                const handleSize = 14 * displayScale;

                if (this.app.pastedData.activeHandle) {
                    const pd = this.app.pastedData;
                    if (pd.activeHandle === 'move') cursor = 'move';
                    else if (pd.activeHandle === 'rotate') {
                        const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><g fill='none' stroke='black' stroke-width='4' stroke-linecap='round' stroke-linejoin='round'><path d='M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8'/><path d='M3 3v5h5'/><path d='M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16'/><path d='M16 16h5v5'/></g><g fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8'/><path d='M3 3v5h5'/><path d='M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16'/><path d='M16 16h5v5'/></g></svg>`;
                        cursor = `url("data:image/svg+xml;base64,${btoa(svg)}") 12 12, crosshair`;
                    }
                    else if (['nw', 'ne', 'sw', 'se'].includes(pd.activeHandle as string)) {
                        cursor = this.getRotatedCursor(pd.activeHandle as any, pd.rotation);
                    }
                } else {
                    // Hover check (Local)
                    if (Math.abs(lx - cx) < handleSize && Math.abs(ly - (py + offset)) < handleSize) {
                        const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><g fill='none' stroke='black' stroke-width='4' stroke-linecap='round' stroke-linejoin='round'><path d='M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8'/><path d='M3 3v5h5'/><path d='M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16'/><path d='M16 16h5v5'/></g><g fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8'/><path d='M3 3v5h5'/><path d='M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16'/><path d='M16 16h5v5'/></g></svg>`;
                        cursor = `url("data:image/svg+xml;base64,${btoa(svg)}") 12 12, crosshair`;
                    }
                    else if (Math.abs(lx - px) < handleSize && Math.abs(ly - py) < handleSize) cursor = this.getRotatedCursor('nw', rot);
                    else if (Math.abs(lx - (px + pw)) < handleSize && Math.abs(ly - py) < handleSize) cursor = this.getRotatedCursor('ne', rot);
                    else if (Math.abs(lx - px) < handleSize && Math.abs(ly - (py + ph)) < handleSize) cursor = this.getRotatedCursor('sw', rot);
                    else if (Math.abs(lx - (px + pw)) < handleSize && Math.abs(ly - (py + ph)) < handleSize) cursor = this.getRotatedCursor('se', rot);
                    else if (lx >= px && lx <= px + pw && ly >= py && ly <= py + ph) cursor = 'move';
                }
                viewCanvas.style.cursor = cursor;

                if (this.app.pastedData.activeHandle) {
                    const pd = this.app.pastedData;

                    if (pd.activeHandle === 'move') {
                        const dx = x - pd.startX;
                        const dy = y - pd.startY;
                        pd.x = pd.startPosX + dx;
                        pd.y = pd.startPosY + dy;

                        const trash = this.app.container.querySelector('#sl-trash-bin') as HTMLElement;
                        trash.style.display = 'flex';
                        const tRect = trash.getBoundingClientRect();
                        const tx = tRect.left + tRect.width / 2;
                        const ty = tRect.top + tRect.height / 2;
                        const distToTrash = Math.hypot(e.clientX - tx, e.clientY - ty);
                        const isOverTrash = e.clientX >= tRect.left && e.clientX <= tRect.right && 
                                            e.clientY >= tRect.top && e.clientY <= tRect.bottom;

                        let tScale = 1.0;
                        const shrinkThreshold = 150;
                        const minScale = 0.2;

                        const iconClosed = trash.querySelector('#sl-trash-icon-closed') as HTMLElement;
                        const iconOpen = trash.querySelector('#sl-trash-icon-open') as HTMLElement;

                        if (isOverTrash) {
                            tScale = minScale;
                            trash.style.background = 'rgba(239, 68, 68, 0.4)';
                            trash.style.transform = 'scale(1.1)';
                            if (iconClosed) iconClosed.style.display = 'none';
                            if (iconOpen) {
                                iconOpen.style.display = 'inline-block';
                                iconOpen.classList.add('fa-shake');
                            }
                        } else {
                            if (distToTrash < shrinkThreshold) {
                                tScale = minScale + (1.0 - minScale) * (distToTrash / shrinkThreshold);
                            }
                            trash.style.background = 'rgba(24, 24, 24, 0.8)';
                            trash.style.transform = 'scale(1)';
                            if (iconClosed) iconClosed.style.display = 'inline-block';
                            if (iconOpen) {
                                iconOpen.style.display = 'none';
                                iconOpen.classList.remove('fa-shake');
                            }
                        }

                        (pd as any).trashScale = tScale;

                        if (tScale < 1.0) {
                            const factor = (1.0 - tScale) / (1.0 - minScale);
                            const targetX = x - pd.startW / 2;
                            const targetY = y - pd.startH / 2;
                            pd.x = pd.x + (targetX - pd.x) * factor;
                            pd.y = pd.y + (targetY - pd.y) * factor;
                        }
                    } else if (pd.activeHandle === 'rotate') {
                        const angle = Math.atan2(y - cy, x - cx);
                        pd.rotation = angle + Math.PI / 2;
                    } else {
                        // SCALING LOGIC
                        const dx = lx - pd.startLX;
                        const dy = ly - pd.startLY;

                        let newW = pd.startW;
                        let newH = pd.startH;

                        if (pd.activeHandle === 'se') { newW = pd.startW + dx; newH = pd.startH + dy; }
                        else if (pd.activeHandle === 'nw') { newW = pd.startW - dx; newH = pd.startH - dy; }
                        else if (pd.activeHandle === 'ne') { newW = pd.startW + dx; newH = pd.startH - dy; }
                        else if (pd.activeHandle === 'sw') { newW = pd.startW - dx; newH = pd.startH + dy; }

                        newW = Math.max(1, newW);
                        newH = Math.max(1, newH);

                        pd.w = newW;
                        pd.h = newH;

                        // Recalculate TL to keep global anchor fixed
                        let alx = 0, aly = 0;
                        if (pd.activeHandle === 'nw') { alx = newW; aly = newH; }
                        else if (pd.activeHandle === 'ne') { alx = 0; aly = newH; }
                        else if (pd.activeHandle === 'sw') { alx = newW; aly = 0; }
                        else if (pd.activeHandle === 'se') { alx = 0; aly = 0; }

                        const lmidX = newW / 2;
                        const lmidY = newH / 2;
                        const dlMidX = lmidX - alx;
                        const dlMidY = lmidY - aly;

                        const cosR = Math.cos(pd.rotation);
                        const sinR = Math.sin(pd.rotation);
                        const newCx = pd.startAncX + (dlMidX * cosR - dlMidY * sinR);
                        const newCy = pd.startAncY + (dlMidX * sinR + dlMidY * cosR);

                        pd.x = newCx - newW / 2;
                        pd.y = newCy - newH / 2;
                    }

                    this.updatePasteOverlay();
                    return;
                }
            } else {
                viewCanvas.style.cursor = (this.app.currentTool === 'mouse') ? 'default' : (this.app.currentTool === 'picker' ? 'none' : 'crosshair');
            }

            this.app.lastSheetX = x;
            this.app.lastSheetY = y;
            this.renderToolPreview(x, y, viewCanvas, dataCanvas);

            // Magnifier logic
            if (this.app.currentTool === 'picker' && this.magnifier && this.magCtx) {
                const ZOOM = 36;
                const PIXELS = 5;
                const SIZE = ZOOM * PIXELS;
                const OFFSET = Math.floor(SIZE / 2);

                this.magnifier.style.display = 'block';
                // Position magnifier centered on cursor
                this.magnifier.style.left = `${e.clientX - OFFSET}px`;
                this.magnifier.style.top = `${e.clientY - OFFSET}px`;

                const tx = Math.floor(x);
                const ty = Math.floor(y);

                this.magCtx.clearRect(0, 0, SIZE, SIZE);
                this.magCtx.imageSmoothingEnabled = false;
                
                const srcX = tx - Math.floor(PIXELS / 2);
                const srcY = ty - Math.floor(PIXELS / 2);

                // Draw zoomed region from dataCanvas
                this.magCtx.drawImage(dataCanvas, srcX, srcY, PIXELS, PIXELS, 0, 0, SIZE, SIZE);

                // Draw Grid
                this.magCtx.lineWidth = 1;
                this.magCtx.strokeStyle = 'rgba(100, 100, 100, 0.4)';
                this.magCtx.beginPath();
                for (let i = 0; i <= PIXELS; i++) {
                    this.magCtx.moveTo(i * ZOOM, 0);
                    this.magCtx.lineTo(i * ZOOM, SIZE);
                    this.magCtx.moveTo(0, i * ZOOM);
                    this.magCtx.lineTo(SIZE, i * ZOOM);
                }
                this.magCtx.stroke();

                // Draw precise Center Box
                const cx = Math.floor(PIXELS / 2) * ZOOM;
                const cy = Math.floor(PIXELS / 2) * ZOOM;
                
                this.magCtx.fillStyle = 'rgba(0,0,0,0.8)';
                this.magCtx.fillRect(cx - 1, cy - 1, ZOOM + 2, 1);
                this.magCtx.fillRect(cx - 1, cy + ZOOM, ZOOM + 2, 1);
                this.magCtx.fillRect(cx - 1, cy - 1, 1, ZOOM + 2);
                this.magCtx.fillRect(cx + ZOOM, cy - 1, 1, ZOOM + 2);

                this.magCtx.fillStyle = '#ffffff';
                this.magCtx.fillRect(cx, cy, ZOOM, 1);
                this.magCtx.fillRect(cx, cy + ZOOM - 1, ZOOM, 1);
                this.magCtx.fillRect(cx, cy, 1, ZOOM);
                this.magCtx.fillRect(cx + ZOOM - 1, cy, 1, ZOOM);
            } else if (this.magnifier) {
                this.magnifier.style.display = 'none';
            }
        };

        viewCanvas.onpointerup = (e: PointerEvent) => {
            if (this.app.pastedData) {
                if (this.app.pastedData.activeHandle === 'move') {
                    const trash = this.app.container.querySelector('#sl-trash-bin') as HTMLElement;
                    const tRect = trash.getBoundingClientRect();
                    const isOverTrash = e.clientX >= tRect.left && e.clientX <= tRect.right && 
                                        e.clientY >= tRect.top && e.clientY <= tRect.bottom;
                    
                    const iconClosed = trash.querySelector('#sl-trash-icon-closed') as HTMLElement;
                    const iconOpen = trash.querySelector('#sl-trash-icon-open') as HTMLElement;

                    if (isOverTrash) {
                        this.app.pastedData = null;
                        this.updatePasteOverlay();
                        this.app.historyManager.takeSnapshot();
                    }

                    trash.style.display = 'none';
                    if (iconClosed) iconClosed.style.display = 'inline-block';
                    if (iconOpen) {
                        iconOpen.style.display = 'none';
                        iconOpen.classList.remove('fa-shake');
                    }
                    if (isOverTrash) return;
                    if (this.app.pastedData) (this.app.pastedData as any).trashScale = 1.0;
                }
                if (this.app.pastedData) this.app.pastedData.activeHandle = null;
                this.updatePasteOverlay();
            }
            this.app.historyManager.takeSnapshot();
        };

        viewCanvas.onpointerleave = () => {
            if (this.app.pastedData) {
                this.app.pastedData.activeHandle = null;
            }
            if (this.magnifier) {
                this.magnifier.style.display = 'none';
            }
            this.renderToolPreview(-1, -1, viewCanvas, dataCanvas);
            viewCanvas.style.cursor = (this.app.currentTool === 'mouse') ? 'default' : 'crosshair';
            this.app.lastSheetX = -1;
            this.app.lastSheetY = -1;
            const p = document.getElementById('sl-tool-preview');
            if (p) p.style.display = 'none';
        };
    }

    handleBrushTool(e: PointerEvent, viewCanvas: HTMLCanvasElement, dataCanvas: HTMLCanvasElement, startX: number, startY: number) {
        const ctx = dataCanvas.getContext('2d');
        if (!ctx) return;

        const isSecondary = e.button === 2 || e.buttons === 2;
        const rect = viewCanvas.getBoundingClientRect();
        const scale = viewCanvas.width / rect.width;

        let lastX = startX;
        let lastY = startY;

        const visitedWidth = dataCanvas.width;
        const visitedHeight = dataCanvas.height;
        const visited = new Uint8Array(visitedWidth * visitedHeight);

        const drawPixel = (tx: number, ty: number, rclick: boolean) => {
            const size = this.app.toolState.brush.size;
            const fx = Math.floor(tx - (size - 1) / 2);
            const fy = Math.floor(ty - (size - 1) / 2);

            // BOUNDARY CHECK if selection active
            if (this.app.selectedFrame) {
                const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
                const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
                const fw = parseInt(wInput?.value || '32');
                const fh = parseInt(hInput?.value || '32');

                const minX = this.app.selectedFrame.col * fw;
                const minY = this.app.selectedFrame.row * fh;

                ctx.save();
                ctx.beginPath();
                ctx.rect(minX, minY, fw, fh);
                ctx.clip();
            }

            ctx.fillStyle = rclick ? this.app.toolState.brush.secondaryColor : this.app.toolState.brush.color;

            if (size <= 2) {
                for (let ix = 0; ix < size; ix++) {
                    for (let iy = 0; iy < size; iy++) {
                        const px = fx + ix;
                        const py = fy + iy;
                        if (px >= 0 && px < visitedWidth && py >= 0 && py < visitedHeight) {
                            const vIdx = py * visitedWidth + px;
                            if (!visited[vIdx]) {
                                visited[vIdx] = 1;
                                if (this.app.toolState.brush.mode === 'transparent') {
                                    ctx.clearRect(px, py, 1, 1);
                                } else {
                                    ctx.fillRect(px, py, 1, 1);
                                }
                            }
                        }
                    }
                }
            } else {
                const radius = (size - 1) / 2;
                const r2 = (size / 2) * (size / 2);
                for (let ix = 0; ix < size; ix++) {
                    for (let iy = 0; iy < size; iy++) {
                        const dx = ix - radius;
                        const dy = iy - radius;
                        if (dx * dx + dy * dy < r2) {
                            const px = fx + ix;
                            const py = fy + iy;
                            if (px >= 0 && px < visitedWidth && py >= 0 && py < visitedHeight) {
                                const vIdx = py * visitedWidth + px;
                                if (!visited[vIdx]) {
                                    visited[vIdx] = 1;
                                    if (this.app.toolState.brush.mode === 'transparent') {
                                        ctx.clearRect(px, py, 1, 1);
                                    } else {
                                        ctx.fillRect(px, py, 1, 1);
                                    }
                                }
                            }
                        }
                    }
                }
            }

            if (this.app.selectedFrame) {
                ctx.restore();
            }
        };

        const drawLine = (x0: number, y0: number, x1: number, y1: number, rclick: boolean) => {
            x0 = Math.floor(x0); y0 = Math.floor(y0);
            x1 = Math.floor(x1); y1 = Math.floor(y1);
            const dx = Math.abs(x1 - x0);
            const dy = Math.abs(y1 - y0);
            const sx = x0 < x1 ? 1 : -1;
            const sy = y0 < y1 ? 1 : -1;
            let err = dx - dy;

            while (true) {
                drawPixel(x0, y0, rclick);
                if (x0 === x1 && y0 === y1) break;
                const e2 = 2 * err;
                if (e2 > -dy) { err -= dy; x0 += sx; }
                if (e2 < dx) { err += dx; y0 += sy; }
            }
        };

        drawPixel(startX, startY, isSecondary);
        this.refreshViewCanvas(viewCanvas, dataCanvas);

        const move = (me: PointerEvent) => {
            me.preventDefault();
            const mx = (me.clientX - rect.left) * scale;
            const my = (me.clientY - rect.top) * scale;
            const moveSecondary = me.buttons === 2;

            if (mx >= 0 && mx < viewCanvas.width && my >= 0 && my < viewCanvas.height) {
                drawLine(lastX, lastY, mx, my, moveSecondary);
                lastX = mx;
                lastY = my;
                this.refreshViewCanvas(viewCanvas, dataCanvas);
            }
        };

        window.addEventListener('pointermove', move);

        const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            window.removeEventListener('sl-cancel-tool', cancel);
            logDev(`[Editor] handleBrushTool committed`);
            this.app.currentBase64 = dataCanvas.toDataURL();
            this.app.updatePreview(this.app.currentBase64, false);
            this.app.historyManager.takeSnapshot();
        };

        const cancel = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            window.removeEventListener('sl-cancel-tool', cancel);
            const img = new Image();
            img.src = this.app.currentBase64;
            img.onload = () => {
                ctx.clearRect(0, 0, dataCanvas.width, dataCanvas.height);
                ctx.drawImage(img, 0, 0);
                this.refreshViewCanvas(viewCanvas, dataCanvas);
            };
        };

        window.addEventListener('pointerup', up, { once: true });
        window.addEventListener('sl-cancel-tool', cancel, { once: true });
    }

    handleEraserTool(e: PointerEvent, viewCanvas: HTMLCanvasElement, dataCanvas: HTMLCanvasElement, startX: number, startY: number) {
        const ctx = dataCanvas.getContext('2d');
        if (!ctx) return;

        const isSecondary = e.button === 2 || e.buttons === 2;
        const rect = viewCanvas.getBoundingClientRect();
        const scale = viewCanvas.width / rect.width;

        let lastX = startX;
        let lastY = startY;

        const visitedWidth = dataCanvas.width;
        const visitedHeight = dataCanvas.height;
        const visited = new Uint8Array(visitedWidth * visitedHeight);

        const erasePixel = (tx: number, ty: number, rclick: boolean) => {
            const size = this.app.toolState.eraser.size;
            const fx = Math.floor(tx - (size - 1) / 2);
            const fy = Math.floor(ty - (size - 1) / 2);

            // BOUNDARY CHECK if selection active
            if (this.app.selectedFrame) {
                const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
                const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
                const fw = parseInt(wInput?.value || '32');
                const fh = parseInt(hInput?.value || '32');

                const minX = this.app.selectedFrame.col * fw;
                const minY = this.app.selectedFrame.row * fh;

                ctx.save();
                ctx.beginPath();
                ctx.rect(minX, minY, fw, fh);
                ctx.clip();
            }

            ctx.fillStyle = rclick ? this.app.toolState.eraser.secondaryColor : this.app.toolState.eraser.color;

            // Eraser is always square
            for (let ix = 0; ix < size; ix++) {
                for (let iy = 0; iy < size; iy++) {
                    const px = fx + ix;
                    const py = fy + iy;
                    if (px >= 0 && px < visitedWidth && py >= 0 && py < visitedHeight) {
                        const vIdx = py * visitedWidth + px;
                        if (!visited[vIdx]) {
                            visited[vIdx] = 1;
                            if (this.app.toolState.eraser.mode === 'transparent') {
                                ctx.clearRect(px, py, 1, 1);
                            } else {
                                ctx.fillRect(px, py, 1, 1);
                            }
                        }
                    }
                }
            }

            if (this.app.selectedFrame) {
                ctx.restore();
            }
        };

        const eraseLine = (x0: number, y0: number, x1: number, y1: number, rclick: boolean) => {
            x0 = Math.floor(x0); y0 = Math.floor(y0);
            x1 = Math.floor(x1); y1 = Math.floor(y1);
            const dx = Math.abs(x1 - x0);
            const dy = Math.abs(y1 - y0);
            const sx = x0 < x1 ? 1 : -1;
            const sy = y0 < y1 ? 1 : -1;
            let err = dx - dy;

            while (true) {
                erasePixel(x0, y0, rclick);
                if (x0 === x1 && y0 === y1) break;
                const e2 = 2 * err;
                if (e2 > -dy) { err -= dy; x0 += sx; }
                if (e2 < dx) { err += dx; y0 += sy; }
            }
        };

        erasePixel(startX, startY, isSecondary);
        this.refreshViewCanvas(viewCanvas, dataCanvas);

        const move = (me: PointerEvent) => {
            me.preventDefault();
            const mx = (me.clientX - rect.left) * scale;
            const my = (me.clientY - rect.top) * scale;
            const moveSecondary = me.buttons === 2;

            if (mx >= 0 && mx < viewCanvas.width && my >= 0 && my < viewCanvas.height) {
                eraseLine(lastX, lastY, mx, my, moveSecondary);
                lastX = mx;
                lastY = my;
                this.refreshViewCanvas(viewCanvas, dataCanvas);
            }
        };

        window.addEventListener('pointermove', move);

        const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            window.removeEventListener('sl-cancel-tool', cancel);
            logDev(`[Editor] handleEraserTool committed`);
            this.app.currentBase64 = dataCanvas.toDataURL();
            this.app.updatePreview(this.app.currentBase64, false);
            this.app.historyManager.takeSnapshot();
        };

        const cancel = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            window.removeEventListener('sl-cancel-tool', cancel);
            const img = new Image();
            img.src = this.app.currentBase64;
            img.onload = () => {
                ctx.clearRect(0, 0, dataCanvas.width, dataCanvas.height);
                ctx.drawImage(img, 0, 0);
                this.refreshViewCanvas(viewCanvas, dataCanvas);
            };
        };

        window.addEventListener('pointerup', up, { once: true });
        window.addEventListener('sl-cancel-tool', cancel, { once: true });
    }

    handleBucketTool(e: PointerEvent, viewCanvas: HTMLCanvasElement, dataCanvas: HTMLCanvasElement, x: number, y: number) {
        const execute = () => {
            const isSecondary = e.button === 2;
            const tx = Math.floor(x);
            const ty = Math.floor(y);

            const dataCtx = dataCanvas.getContext('2d');
            if (!dataCtx) return;

        const imgData = dataCtx.getImageData(0, 0, dataCanvas.width, dataCanvas.height);
        const data = imgData.data;

        const getPixel = (px: number, py: number) => {
            const idx = (py * dataCanvas.width + px) * 4;
            return [data[idx], data[idx + 1], data[idx + 2], data[idx + 3]];
        };

        const targetColor = getPixel(tx, ty);
        const fillHex = isSecondary ? this.app.toolState.brush.secondaryColor : this.app.toolState.brush.color;
        const isTransparent = this.app.toolState.brush.mode === 'transparent';

        // Convert hex to RGBA
        const hexToRgba = (hex: string) => {
            if (hex.length === 4) {
                const r = parseInt(hex[1] + hex[1], 16);
                const g = parseInt(hex[2] + hex[2], 16);
                const b = parseInt(hex[3] + hex[3], 16);
                return [r, g, b, 255];
            }
            if (hex.length === 9) {
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                const a = parseInt(hex.slice(7, 9), 16);
                return [r, g, b, a];
            }
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return [r, g, b, 255];
        };
        const fillColor = isTransparent ? [0, 0, 0, 0] : hexToRgba(fillHex);

        const tolerance = this.app.toolState.bucket.tolerance || 0;
        const maxDist = (tolerance / 100) * 441.67; // Max Euclidean distance in RGB is sqrt(255^2 * 3)

        const isMatch = (r: number, g: number, b: number, a: number) => {
            if (tolerance === 0) {
                return r === targetColor[0] && g === targetColor[1] && b === targetColor[2] && a === targetColor[3];
            }
            // Simple Euclidean distance for RGB
            const dr = r - targetColor[0];
            const dg = g - targetColor[1];
            const db = b - targetColor[2];
            const dist = Math.sqrt(dr * dr + dg * dg + db * db);

            // For Alpha, we'll require exact match or very close if we want to be fancy, 
            // but usually bucket tolerance in pixel editors focuses on RGB.
            // Let's stick to RGB distance for now, but require alpha to be similar.
            const da = a - targetColor[3];
            return dist <= maxDist && Math.abs(da) < 10;
        };

        // If same color, abort (only if tolerance is 0, otherwise it depends on the match)
        if (tolerance === 0 && targetColor[0] === fillColor[0] && targetColor[1] === fillColor[1] && targetColor[2] === fillColor[2] && targetColor[3] === fillColor[3]) {
            return;
        }

        // Boundary check (selected frame)
        let minX = 0, minY = 0, maxX = dataCanvas.width, maxY = dataCanvas.height;
        if (this.app.selectedFrame) {
            const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
            const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
            const fw = parseInt(wInput?.value || '32');
            const fh = parseInt(hInput?.value || '32');
            minX = this.app.selectedFrame.col * fw;
            minY = this.app.selectedFrame.row * fh;
            maxX = minX + fw;
            maxY = minY + fh;

            if (tx < minX || tx >= maxX || ty < minY || ty >= maxY) {
                return; // Abort if initial click is outside the frame
            }
        }

        const visited = new Uint8Array(dataCanvas.width * dataCanvas.height);

        // Flood fill algorithm
        const stack: [number, number][] = [[tx, ty]];
        while (stack.length > 0) {
            const [cx, cy] = stack.pop()!;
            const idx = (cy * dataCanvas.width + cx) * 4;
            const vIdx = cy * dataCanvas.width + cx;

            if (!visited[vIdx] && isMatch(data[idx], data[idx + 1], data[idx + 2], data[idx + 3])) {
                visited[vIdx] = 1;

                if (fillColor[3] === 255 || fillColor[3] === 0 || isTransparent) {
                    data[idx] = fillColor[0];
                    data[idx + 1] = fillColor[1];
                    data[idx + 2] = fillColor[2];
                    data[idx + 3] = fillColor[3];
                } else {
                    const a1 = fillColor[3];
                    const r1 = fillColor[0];
                    const g1 = fillColor[1];
                    const b1 = fillColor[2];

                    const a2 = data[idx + 3];
                    const r2 = data[idx];
                    const g2 = data[idx + 1];
                    const b2 = data[idx + 2];

                    const alphaSrc = a1 / 255;
                    const alphaDst = a2 / 255;
                    const outAlpha = alphaSrc + alphaDst * (1 - alphaSrc);

                    if (outAlpha > 0) {
                        data[idx] = Math.round((r1 * alphaSrc + r2 * alphaDst * (1 - alphaSrc)) / outAlpha);
                        data[idx + 1] = Math.round((g1 * alphaSrc + g2 * alphaDst * (1 - alphaSrc)) / outAlpha);
                        data[idx + 2] = Math.round((b1 * alphaSrc + b2 * alphaDst * (1 - alphaSrc)) / outAlpha);
                        data[idx + 3] = Math.round(outAlpha * 255);
                    } else {
                        data[idx] = 0;
                        data[idx + 1] = 0;
                        data[idx + 2] = 0;
                        data[idx + 3] = 0;
                    }
                }

                if (cx + 1 < maxX) stack.push([cx + 1, cy]);
                if (cx - 1 >= minX) stack.push([cx - 1, cy]);
                if (cy + 1 < maxY) stack.push([cx, cy + 1]);
                if (cy - 1 >= minY) stack.push([cx, cy - 1]);
            }
        }

            dataCtx.putImageData(imgData, 0, 0);

            this.refreshViewCanvas(viewCanvas, dataCanvas);

            logDev(`[Editor] handleBucketTool committed`);
            this.app.currentBase64 = dataCanvas.toDataURL();
            this.app.updatePreview(this.app.currentBase64, false);
            this.app.historyManager.takeSnapshot();
        };

        if (e.pointerType === 'touch') {
            let cancelled = false;
            const cancel = () => cancelled = true;
            window.addEventListener('sl-cancel-tool', cancel, { once: true });
            setTimeout(() => {
                window.removeEventListener('sl-cancel-tool', cancel);
                if (!cancelled) execute();
            }, 50);
        } else {
            execute();
        }
    }

    handlePickerTool(e: PointerEvent, viewCanvas: HTMLCanvasElement, dataCanvas: HTMLCanvasElement, startX: number, startY: number) {
        const dataCtx = dataCanvas.getContext('2d');
        if (!dataCtx) return;

        const isSecondary = e.button === 2 || e.buttons === 2;

        const updateColor = (x: number, y: number, finalize: boolean) => {
            const tx = Math.floor(x);
            const ty = Math.floor(y);
            if (tx < 0 || ty < 0 || tx >= dataCanvas.width || ty >= dataCanvas.height) return;

            const imgData = dataCtx.getImageData(tx, ty, 1, 1);
            const data = imgData.data;

            if (data[3] === 0) {
                if (finalize) {
                    if (this.app.colorPicker?.isOpen()) {
                        this.app.colorPicker.close();
                    }
                    this.switchTool(this.app.previousTool);
                }
                return;
            }

            const hex = '#' + [data[0], data[1], data[2]].map(c => c.toString(16).padStart(2, '0')).join('');

            if (this.app.colorPicker?.isOpen()) {
                this.app.colorPicker.setColor(hex);
                this.app.colorPicker.triggerChange();
                if (finalize) {
                    this.app.colorPicker.close();
                    this.switchTool(this.app.previousTool);
                }
                return;
            }

            if (isSecondary) {
                this.app.toolState.brush.secondaryColor = hex;
                const btn = this.app.container.querySelector('#sl-brush-color-secondary') as HTMLElement;
                if (btn) btn.style.backgroundColor = hex;
            } else {
                this.app.toolState.brush.color = hex;
                const btn = this.app.container.querySelector('#sl-brush-color') as HTMLElement;
                if (btn) btn.style.backgroundColor = hex;
            }

            if (finalize) {
                this.switchTool(this.app.previousTool);
            }
        };

        // Initial pick
        updateColor(startX, startY, false);

        const onPointerMove = (moveEvent: PointerEvent) => {
            const rect = viewCanvas.getBoundingClientRect();
            const scale = viewCanvas.width / rect.width;
            const currentX = (moveEvent.clientX - rect.left) * scale;
            const currentY = (moveEvent.clientY - rect.top) * scale;
            updateColor(currentX, currentY, false);
        };

        const onPointerUp = (upEvent: PointerEvent) => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('sl-cancel-tool', cancel);
            const rect = viewCanvas.getBoundingClientRect();
            const scale = viewCanvas.width / rect.width;
            const currentX = (upEvent.clientX - rect.left) * scale;
            const currentY = (upEvent.clientY - rect.top) * scale;
            updateColor(currentX, currentY, true);
        };

        const cancel = () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('sl-cancel-tool', cancel);
        };

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('sl-cancel-tool', cancel, { once: true });
    }


    handleMouseTool(e: PointerEvent, viewCanvas: HTMLCanvasElement, dataCanvas: HTMLCanvasElement, fw: number, fh: number, targetW: number, targetH: number, x: number, y: number) {
        const dataCtx = dataCanvas.getContext('2d');
        if (!dataCtx) return;

        const rect = viewCanvas.getBoundingClientRect();
        const visualScale = rect.width / viewCanvas.width;

        const isInsideSelection = () => {
            if (!this.app.selectedFrame) return false;
            if (this.app.selectedFrame.free && this.app.selectedFrame.px !== undefined) {
                return x >= this.app.selectedFrame.px && x <= this.app.selectedFrame.px + this.app.selectedFrame.pw! &&
                    y >= this.app.selectedFrame.py! && y <= this.app.selectedFrame.py! + this.app.selectedFrame.ph!;
            } else {
                return x >= this.app.selectedFrame.col * fw && x < (this.app.selectedFrame.col + 1) * fw &&
                    y >= this.app.selectedFrame.row * fh && y < (this.app.selectedFrame.row + 1) * fh;
            }
        };

        const movingSelection = isInsideSelection();

        let sourceX = 0;
        let sourceY = 0;
        let pWidth = fw;
        let pHeight = fh;

        let spriteData: ImageData | null = null;
        let ghost: HTMLCanvasElement | null = null;
        let grabOffsetX = 0;
        let grabOffsetY = 0;

        if (movingSelection && this.app.selectedFrame) {
            if (this.app.selectedFrame.free && this.app.selectedFrame.px !== undefined) {
                sourceX = this.app.selectedFrame.px;
                sourceY = this.app.selectedFrame.py!;
                pWidth = this.app.selectedFrame.pw!;
                pHeight = this.app.selectedFrame.ph!;
            } else {
                sourceX = this.app.selectedFrame.col * fw;
                sourceY = this.app.selectedFrame.row * fh;
                pWidth = fw;
                pHeight = fh;
            }

            spriteData = dataCtx.getImageData(sourceX, sourceY, pWidth, pHeight);

            grabOffsetX = (x - sourceX) * visualScale;
            grabOffsetY = (y - sourceY) * visualScale;

            ghost = document.createElement('canvas');
            ghost.width = pWidth;
            ghost.height = pHeight;
            ghost.getContext('2d')!.putImageData(spriteData, 0, 0);

            ghost.style.cssText = `
                position: fixed; pointer-events: none; z-index: 20000;
                width: ${pWidth * visualScale}px;
                height: ${pHeight * visualScale}px;
                image-rendering: pixelated;
                opacity: 0.8;
                outline: 2px solid #0078d4;
                box-shadow: 0 0 15px rgba(99, 102, 241, 0.6);
                transform-origin: center center;
                transition: transform 0.1s ease-out;
            `;
            document.body.appendChild(ghost);
        }

        // Trash Logic
        const trash = this.app.container.querySelector('#sl-trash-bin') as HTMLElement;
        if (movingSelection) {
            trash.style.display = 'flex';
        }

        const updateGhostPos = (clientX: number, clientY: number) => {
            if (!ghost) return;
            const tRect = trash.getBoundingClientRect();
            const tx = tRect.left + tRect.width / 2;
            const ty = tRect.top + tRect.height / 2;
            const distToTrash = Math.hypot(clientX - tx, clientY - ty);
            const isOverTrash = clientX >= tRect.left && clientX <= tRect.right && 
                                clientY >= tRect.top && clientY <= tRect.bottom;

            let scale = 1.0;
            const shrinkThreshold = 150;
            const minScale = 0.2;

            if (isOverTrash) {
                scale = minScale;
            } else if (distToTrash < shrinkThreshold) {
                scale = minScale + (1.0 - minScale) * (distToTrash / shrinkThreshold);
            }

            const factor = scale < 1.0 ? (1.0 - scale) / (1.0 - minScale) : 0;
            const centerOffsetX = (pWidth * visualScale) / 2;
            const centerOffsetY = (pHeight * visualScale) / 2;
            const currentOffsetX = grabOffsetX + (centerOffsetX - grabOffsetX) * factor;
            const currentOffsetY = grabOffsetY + (centerOffsetY - grabOffsetY) * factor;

            ghost.style.left = `${clientX - currentOffsetX}px`;
            ghost.style.top = `${clientY - currentOffsetY}px`;
            ghost.style.transform = `scale(${scale})`;
        };

        if (ghost) {
            updateGhostPos(e.clientX, e.clientY);
        }

        const startTime = Date.now();
        const startClientX = e.clientX;
        const startClientY = e.clientY;

        // Create (or reuse) Drag Highlight Rect in SVG
        const svgInfo = this.app.container.querySelector('#sl-sheet-grid');
        let highlightRect = document.getElementById('sl-drag-highlight') as SVGRectElement | null;
        let innerHighlight = document.getElementById('sl-drag-highlight-inner') as SVGRectElement | null;

        if (!highlightRect && svgInfo) {
            highlightRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            highlightRect.id = 'sl-drag-highlight';
            highlightRect.setAttribute('fill', 'rgba(249, 115, 22, 0.1)'); // Subtle orange tint
            highlightRect.setAttribute('stroke', '#000');
            highlightRect.setAttribute('stroke-width', '8');
            highlightRect.setAttribute('vector-effect', 'non-scaling-stroke');
            highlightRect.style.pointerEvents = 'none';
            highlightRect.style.display = 'none';
            svgInfo.appendChild(highlightRect);
        }

        if (!innerHighlight && svgInfo) {
            innerHighlight = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            innerHighlight.id = 'sl-drag-highlight-inner';
            innerHighlight.setAttribute('fill', 'none');
            innerHighlight.setAttribute('stroke', 'rgba(209, 149, 81, 1)'); // Orange
            innerHighlight.setAttribute('stroke-width', '6');
            innerHighlight.setAttribute('stroke-dasharray', '16, 16');
            innerHighlight.setAttribute('vector-effect', 'non-scaling-stroke');
            innerHighlight.style.pointerEvents = 'none';
            innerHighlight.style.display = 'none';

            if (!document.getElementById('sl-svg-anim-style')) {
                const s = document.createElement('style');
                s.id = 'sl-svg-anim-style';
                s.textContent = `
                    #sl-drag-highlight-inner {
                        animation: sl-svg-march 0.5s linear infinite;
                    }
                    @keyframes sl-svg-march {
                        to { stroke-dashoffset: 32; }
                    }
                `;
                document.head.appendChild(s);
            }
            svgInfo.appendChild(innerHighlight);
        }

        const move = (me: PointerEvent) => {
            me.preventDefault();
            const curRect = viewCanvas.getBoundingClientRect();
            const curScale = curRect.width / viewCanvas.width;

            const mx = (me.clientX - curRect.left) * (1 / curScale);
            const my = (me.clientY - curRect.top) * (1 / curScale);

            if (movingSelection && ghost) {
                ghost.style.width = `${pWidth * curScale}px`;
                ghost.style.height = `${pHeight * curScale}px`;
                updateGhostPos(me.clientX, me.clientY);

                if (mx >= 0 && mx < viewCanvas.width && my >= 0 && my < viewCanvas.height) {
                    const snapX = Math.floor(mx);
                    const snapY = Math.floor(my);

                    let targetX = snapX;
                    let targetY = snapY;
                    let targetW = pWidth;
                    let targetH = pHeight;

                    // If moving a full frame, snap to grid
                    if (this.app.selectedFrame && !this.app.selectedFrame.free) {
                        const tCol = Math.floor(mx / fw);
                        const tRow = Math.floor(my / fh);
                        targetX = tCol * fw;
                        targetY = tRow * fh;
                        targetW = fw;
                        targetH = fh;
                    }

                    if (highlightRect) {
                        highlightRect.style.display = 'block';
                        const scale = this.app?.transformState?.scale || 1;
                        const inset = 3.5 / scale;
                        highlightRect.setAttribute('x', `${targetX + inset}`);
                        highlightRect.setAttribute('y', `${targetY + inset}`);
                        highlightRect.setAttribute('width', `${targetW - inset * 2}`);
                        highlightRect.setAttribute('height', `${targetH - inset * 2}`);
                    }
                    if (innerHighlight) {
                        innerHighlight.style.display = 'block';
                        const scale = this.app?.transformState?.scale || 1;
                        const inset = 3.5 / scale;
                        innerHighlight.setAttribute('x', `${targetX + inset}`);
                        innerHighlight.setAttribute('y', `${targetY + inset}`);
                        innerHighlight.setAttribute('width', `${targetW - inset * 2}`);
                        innerHighlight.setAttribute('height', `${targetH - inset * 2}`);
                    }
                } else {
                    if (highlightRect) highlightRect.style.display = 'none';
                    if (innerHighlight) innerHighlight.style.display = 'none';
                }

                const tRect = trash.getBoundingClientRect();
                const isOverTrash = me.clientX >= tRect.left && me.clientX <= tRect.right &&
                                    me.clientY >= tRect.top && me.clientY <= tRect.bottom;

                const iconClosed = trash.querySelector('#sl-trash-icon-closed') as HTMLElement;
                const iconOpen = trash.querySelector('#sl-trash-icon-open') as HTMLElement;

                if (isOverTrash) {
                    trash.style.background = 'rgba(239, 68, 68, 0.4)';
                    trash.style.transform = 'scale(1.1)';
                    if (iconClosed) iconClosed.style.display = 'none';
                    if (iconOpen) {
                        iconOpen.style.display = 'inline-block';
                        iconOpen.classList.add('fa-shake');
                    }
                } else {
                    trash.style.background = 'rgba(24, 24, 24, 0.8)';
                    trash.style.transform = 'scale(1)';
                    if (iconClosed) iconClosed.style.display = 'inline-block';
                    if (iconOpen) {
                        iconOpen.style.display = 'none';
                        iconOpen.classList.remove('fa-shake');
                    }
                }
            } else {
                // Creating Free Selection
                const dist = Math.hypot(me.clientX - startClientX, me.clientY - startClientY);
                if (dist > 5) { // Threshold to start drawing selection
                    const startX_internal = (startClientX - curRect.left) * (1 / curScale);
                    const startY_internal = (startClientY - curRect.top) * (1 / curScale);

                    const minX = Math.min(startX_internal, mx);
                    const minY = Math.min(startY_internal, my);
                    const drawW = Math.abs(mx - startX_internal);
                    const drawH = Math.abs(my - startY_internal);

                    if (highlightRect) {
                        highlightRect.style.display = 'block';
                        highlightRect.setAttribute('x', `${minX}`);
                        highlightRect.setAttribute('y', `${minY}`);
                        highlightRect.setAttribute('width', `${drawW}`);
                        highlightRect.setAttribute('height', `${drawH}`);
                    }
                    if (innerHighlight) {
                        innerHighlight.style.display = 'block';
                        innerHighlight.setAttribute('x', `${minX}`);
                        innerHighlight.setAttribute('y', `${minY}`);
                        innerHighlight.setAttribute('width', `${drawW}`);
                        innerHighlight.setAttribute('height', `${drawH}`);
                    }
                }
            }
        };
        window.addEventListener('pointermove', move);

        const up = (ue: PointerEvent) => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            window.removeEventListener('sl-cancel-tool', cancel);
            if (ghost && document.body.contains(ghost)) {
                document.body.removeChild(ghost);
            }
            if (highlightRect) {
                highlightRect.style.display = 'none';
            }
            if (innerHighlight) innerHighlight.style.display = 'none';

            const tRect = trash.getBoundingClientRect();
            const droppedInTrash = ue.clientX >= tRect.left && ue.clientX <= tRect.right &&
                                   ue.clientY >= tRect.top && ue.clientY <= tRect.bottom;

            trash.style.display = 'none';
            const iconClosed = trash.querySelector('#sl-trash-icon-closed') as HTMLElement;
            const iconOpen = trash.querySelector('#sl-trash-icon-open') as HTMLElement;
            if (iconClosed) iconClosed.style.display = 'inline-block';
            if (iconOpen) {
                iconOpen.style.display = 'none';
                iconOpen.classList.remove('fa-shake');
            }

            const duration = Date.now() - startTime;
            const dist = Math.hypot(ue.clientX - startClientX, ue.clientY - startClientY);

            // SELECT logic (Single Click)
            if (duration < 300 && dist < 10) {
                const col = Math.floor(x / fw);
                const row = Math.floor(y / fh);

                // If there's an active selection, and the user single clicks anywhere, just deselect.
                if (this.app.selectedFrame) {
                    this.app.selectedFrame = null;
                } else {
                    this.app.selectedFrame = { col, row };
                }
                this.updateSelectionHighlight(fw, fh);
                return;
            }

            if (movingSelection && spriteData) {
                if (droppedInTrash) {
                    dataCtx.clearRect(sourceX, sourceY, pWidth, pHeight);
                    this.app.selectedFrame = null;
                    this.updateSelectionHighlight(fw, fh);
                    this.applyChangesAndRefresh(dataCanvas, fw, fh, targetW, targetH);
                    return;
                }

                const uRect = viewCanvas.getBoundingClientRect();
                const boundedX = Math.max(0, Math.min(uRect.width - 0.1, ue.clientX - uRect.left));
                const boundedY = Math.max(0, Math.min(uRect.height - 0.1, ue.clientY - uRect.top));

                const ux = boundedX * (viewCanvas.width / uRect.width);
                const uy = boundedY * (viewCanvas.height / uRect.height);

                if (ux >= 0 && ux < viewCanvas.width && uy >= 0 && uy < viewCanvas.height) {
                    let snapX = Math.floor(ux);
                    let snapY = Math.floor(uy);
                    let tCol = Math.floor(ux / fw);
                    let tRow = Math.floor(uy / fh);

                    if (this.app.selectedFrame && !this.app.selectedFrame.free) {
                        snapX = tCol * fw;
                        snapY = tRow * fh;
                    }

                    if (!ue.ctrlKey) {
                        // Context-aware: swap if target is not empty, copy if it is
                        const targetData = dataCtx.getImageData(snapX, snapY, pWidth, pHeight);
                        const pixels = targetData.data;
                        let targetIsEmpty = true;
                        for (let i = 3; i < pixels.length; i += 4) {
                            if (pixels[i] > 0) { targetIsEmpty = false; break; }
                        }

                        if (!targetIsEmpty && (snapX !== sourceX || snapY !== sourceY)) {
                            // SWAP behavior
                            dataCtx.putImageData(targetData, sourceX, sourceY);
                            dataCtx.putImageData(spriteData, snapX, snapY);
                        } else {
                            // COPY behavior (to empty or same location)
                            dataCtx.putImageData(spriteData, snapX, snapY);
                        }
                    } else {
                        // Ctrl-drag is always copy
                        dataCtx.putImageData(spriteData, snapX, snapY);
                    }

                    if (this.app.selectedFrame && this.app.selectedFrame.free) {
                        this.app.selectedFrame.px = snapX;
                        this.app.selectedFrame.py = snapY;
                    } else if (this.app.selectedFrame) {
                        this.app.selectedFrame.col = tCol;
                        this.app.selectedFrame.row = tRow;
                    }
                    this.updateSelectionHighlight(fw, fh);
                    this.applyChangesAndRefresh(dataCanvas, fw, fh, targetW, targetH);
                }
            } else if (!movingSelection) {
                // Finish creating Free Selection -> Convert to Pasted Element
                if (dist >= 10) {
                    const curRect = viewCanvas.getBoundingClientRect();
                    const curScale = curRect.width / viewCanvas.width;
                    const startX_internal = (startClientX - curRect.left) * (1 / curScale);
                    const startY_internal = (startClientY - curRect.top) * (1 / curScale);
                    const endX_internal = (ue.clientX - curRect.left) * (1 / curScale);
                    const endY_internal = (ue.clientY - curRect.top) * (1 / curScale);

                    const clampedStartX = Math.max(0, Math.min(viewCanvas.width, startX_internal));
                    const clampedStartY = Math.max(0, Math.min(viewCanvas.height, startY_internal));
                    const clampedEndX = Math.max(0, Math.min(viewCanvas.width, endX_internal));
                    const clampedEndY = Math.max(0, Math.min(viewCanvas.height, endY_internal));

                    const minX = Math.floor(Math.min(clampedStartX, clampedEndX));
                    const minY = Math.floor(Math.min(clampedStartY, clampedEndY));
                    const drawW = Math.ceil(Math.abs(clampedEndX - clampedStartX));
                    const drawH = Math.ceil(Math.abs(clampedEndY - clampedStartY));

                    if (drawW > 0 && drawH > 0) {
                        // Extract ImageData
                        const selData = dataCtx.getImageData(minX, minY, drawW, drawH);

                        // Clear the original area (CUT behavior)
                        dataCtx.clearRect(minX, minY, drawW, drawH);
                        this.app.currentBase64 = dataCanvas.toDataURL();
                        this.app.updatePreview(this.app.currentBase64, false);

                        // Create an Image from the extracted data
                        const tempCanvas = document.createElement('canvas');
                        tempCanvas.width = drawW;
                        tempCanvas.height = drawH;
                        tempCanvas.getContext('2d')!.putImageData(selData, 0, 0);

                        const img = new Image();
                        img.onload = () => {
                            this.app.selectedFrame = null;
                            const updateFw = parseInt((this.app.container.querySelector('#sl-meta-width') as HTMLInputElement).value) || 32;
                            const updateFh = parseInt((this.app.container.querySelector('#sl-meta-height') as HTMLInputElement).value) || 32;
                            this.updateSelectionHighlight(updateFw, updateFh);

                            this.app.pastedData = {
                                img,
                                x: minX,
                                y: minY,
                                w: drawW,
                                h: drawH,
                                activeHandle: null,
                                startX: 0,
                                startY: 0,
                                startW: drawW,
                                startH: drawH,
                                startPosX: 0,
                                startPosY: 0,
                                startAncX: 0,
                                startAncY: 0,
                                startLX: 0,
                                startLY: 0,
                                rotation: 0,
                                startRotation: 0
                            };
                            if (viewCanvas) this.renderToolPreview(this.app.lastSheetX, this.app.lastSheetY, viewCanvas, dataCanvas);
                            this.app.editorManager.updatePasteOverlay();
                            logDev(`[Editor] Converted free selection to pastedData`);
                        };
                        img.src = tempCanvas.toDataURL();
                    }
                }
            }
        };

        const cancel = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            window.removeEventListener('sl-cancel-tool', cancel);
            if (ghost && document.body.contains(ghost)) {
                document.body.removeChild(ghost);
            }
            if (highlightRect) {
                highlightRect.style.display = 'none';
            }
            if (innerHighlight) innerHighlight.style.display = 'none';
            trash.style.display = 'none';
        };

        window.addEventListener('pointerup', up, { once: true });
        window.addEventListener('sl-cancel-tool', cancel, { once: true });
    }

    updateEnvironment() {
        const bgColorInput = this.app.container.querySelector('#sl-bg-color-text') as HTMLInputElement;
        const color = bgColorInput.value;
        const previewWrap = this.app.container.querySelector('#sl-preview-wrap') as HTMLElement;
        if (previewWrap) {
            previewWrap.style.background = color;
        }

        // Sync Sprite Position and Camera Pivot
        this.updateSpritePosition();
    }

    updateSpritePosition() {
        if (!this.app.currentAnim || !this.app.previewApp) return;

        const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
        const fw = parseInt(wInput?.value || '32');
        const fh = parseInt(hInput?.value || '32');

        this.app.currentAnim.pivot.set(fw / 2, fh / 2);
    }

    setupDOMPanZoom(container: HTMLElement, target: HTMLElement) {
        let isDragging = false;
        let startX = 0;
        let startY = 0;
        let dragStartTime = 0;

        let dragStartX = 0;
        let dragStartY = 0;
        let isBackgroundClick = false;
        this.app.updateSheetTransform = () => {
            const scale = this.app.transformState.scale;
            const targetW = parseFloat(target.getAttribute('data-w') || '0');
            const targetH = parseFloat(target.getAttribute('data-h') || '0');
            
            if (targetW > 0 && targetH > 0) {
                target.style.width = `${targetW * scale}px`;
                target.style.height = `${targetH * scale}px`;
                target.style.transform = `translate(${this.app.transformState.pX}px, ${this.app.transformState.pY}px)`;
            } else {
                target.style.transform = `translate(${this.app.transformState.pX}px, ${this.app.transformState.pY}px)`;
            }
            target.style.setProperty('--sl-inverse-scale', (1 / scale).toFixed(5));

            const fw = parseInt((document.getElementById('sl-meta-width') as HTMLInputElement)?.value) || 32;
            const fh = parseInt((document.getElementById('sl-meta-height') as HTMLInputElement)?.value) || 32;
            this.updateSelectionHighlight(fw, fh);
        };

        container.addEventListener('wheel', (e) => {
            e.preventDefault();
            const delta = -e.deltaY * 0.005; // Increased sensitivity (was 0.001)
            this.app.transformState.scale = Math.min(Math.max(0.1, this.app.transformState.scale + delta), 20); // Increased max scale for pixel peeping
            if (this.app.updateSheetTransform) this.app.updateSheetTransform();
        }, { passive: false });

        const activePointers = new Map<number, { x: number, y: number }>();
        let initialPinchDistance = 0;
        let initialPinchZoom = 1.0;
        let lastMidpoint = { x: 0, y: 0 };

        container.addEventListener('pointerdown', (e) => {
            // Allow clicking on container OR the frame OR CANVAS
            const isCanvas = (e.target as HTMLElement).tagName === 'CANVAS';
            if (e.target !== container && e.target !== target && !isCanvas) return;
            
            activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
            e.preventDefault();

            if (activePointers.size === 2) {
                // Cancel any ongoing tool drawing/action from the first finger
                window.dispatchEvent(new Event('sl-cancel-tool'));

                // Pinch to zoom and pan started
                const ptrs = Array.from(activePointers.values());
                initialPinchDistance = Math.hypot(ptrs[0].x - ptrs[1].x, ptrs[0].y - ptrs[1].y);
                initialPinchZoom = this.app.transformState.scale;
                isDragging = true;
                lastMidpoint = { x: (ptrs[0].x + ptrs[1].x) / 2, y: (ptrs[0].y + ptrs[1].y) / 2 };
            } else if (activePointers.size === 1) {
                if (isCanvas) return; // Do not start single-finger background pan on canvas
                
                isDragging = true;
                dragStartTime = Date.now();
                dragStartX = e.clientX;
                dragStartY = e.clientY;
                startX = e.clientX - this.app.transformState.pX;
                startY = e.clientY - this.app.transformState.pY;
                container.style.cursor = 'grabbing';

                // Determine if clicking on background or "working area"
                const canvas = target.querySelector('canvas');
                const rect = canvas ? canvas.getBoundingClientRect() : target.getBoundingClientRect();
                const insideArea = e.clientX >= rect.left && e.clientX <= rect.right &&
                    e.clientY >= rect.top && e.clientY <= rect.bottom;
                isBackgroundClick = !insideArea;
            }
            container.setPointerCapture(e.pointerId);
        });

        window.addEventListener('pointermove', (e) => {
            if (activePointers.has(e.pointerId)) {
                activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
                if (activePointers.size >= 1) e.preventDefault();
            }

            if (activePointers.size === 2) {
                // Handle Pinch Zoom and Pan
                const ptrs = Array.from(activePointers.values());
                const currentDistance = Math.hypot(ptrs[0].x - ptrs[1].x, ptrs[0].y - ptrs[1].y);
                const currentMidpoint = { x: (ptrs[0].x + ptrs[1].x) / 2, y: (ptrs[0].y + ptrs[1].y) / 2 };

                if (initialPinchDistance > 0) {
                    const zoomFactor = currentDistance / initialPinchDistance;
                    let newZoom = Math.max(0.1, Math.min(initialPinchZoom * zoomFactor, 20)); // match bounds from wheel event
                    this.app.transformState.scale = newZoom;

                    const dx = currentMidpoint.x - lastMidpoint.x;
                    const dy = currentMidpoint.y - lastMidpoint.y;
                    this.app.transformState.pX += dx;
                    this.app.transformState.pY += dy;
                    lastMidpoint = currentMidpoint;

                    if (this.app.updateSheetTransform) this.app.updateSheetTransform();
                }
            } else if (isDragging && activePointers.size === 1) {
                // Handle Pan
                this.app.transformState.pX = e.clientX - startX;
                this.app.transformState.pY = e.clientY - startY;
                if (this.app.updateSheetTransform) this.app.updateSheetTransform();
            }
        });

        const onPointerUp = (e: PointerEvent) => {
            activePointers.delete(e.pointerId);

            if (activePointers.size < 2) {
                initialPinchDistance = 0;
            }

            if (activePointers.size === 0) {
                isDragging = false;
                container.style.cursor = 'grab';
                container.releasePointerCapture(e.pointerId);

                const duration = Date.now() - dragStartTime;
                const dist = Math.hypot(e.clientX - dragStartX, e.clientY - dragStartY);

                // Reset zoom/pan if it was a quick click with minimal movement AND on background
                if (duration < 300 && dist < 10 && isBackgroundClick) {
                    this.app.transformState.scale = this.app.defaultScale;
                    this.app.transformState.pX = 0;
                    this.app.transformState.pY = 0;
                    if (this.app.updateSheetTransform) this.app.updateSheetTransform();
                }
            } else if (activePointers.size === 1) {
                // Re-enable panning for the remaining finger
                const remainingPtr = Array.from(activePointers.values())[0];
                isDragging = true;
                startX = remainingPtr.x - this.app.transformState.pX;
                startY = remainingPtr.y - this.app.transformState.pY;
            }
        };

        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);
    }

    resizeHandle = () => {
        if (!this.app.previewContainer || !this.app.previewApp) return;
        const w = this.app.previewContainer.clientWidth;
        const h = this.app.previewContainer.clientHeight;
        if (w <= 0 || h <= 0) return;

        // Resize Renderer
        this.app.previewApp.renderer.resize(w, h);

        // Calculate Scale & Center
        let baseScale = 2.0; // Integer scale for pixel art
        
        const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
        const fw = parseInt(wInput?.value || '32');
        const fh = parseInt(hInput?.value || '32');
        if (fw > 0 && fh > 0) {
            const fitScale = Math.min(w / fw, h / fh) * 0.95;
            baseScale = fitScale;
        }

        const finalScale = baseScale * this.app.cameraState.zoom;

        this.app.previewApp.stage.scale.set(finalScale);

        // Center
        const defaultX = w / 2;
        const defaultY = h / 2;

        this.app.previewApp.stage.x = defaultX + this.app.cameraState.offset.x;
        this.app.previewApp.stage.y = defaultY + this.app.cameraState.offset.y;
    };

    setupPixiPanZoom(container: HTMLElement) {
        container.style.touchAction = 'none';

        const activePointers = new Map<number, { x: number, y: number }>();
        let initialPinchDistance = 0;
        let initialPinchZoom = 1.0;

        container.addEventListener('pointerdown', (e) => {
            // Do not intercept if clicking on UI overlays like the color picker
            const target = e.target as HTMLElement;
            if (target.closest('button') || target.tagName === 'INPUT') return;

            activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
            e.preventDefault();
            container.setPointerCapture(e.pointerId);

            if (activePointers.size === 2) {
                // Pinch to zoom and pan started
                const ptrs = Array.from(activePointers.values());
                initialPinchDistance = Math.hypot(ptrs[0].x - ptrs[1].x, ptrs[0].y - ptrs[1].y);
                initialPinchZoom = this.app.cameraState.zoom;
                this.app.cameraState.isDragging = true;
                lastMidpoint = { x: (ptrs[0].x + ptrs[1].x) / 2, y: (ptrs[0].y + ptrs[1].y) / 2 };
            } else if (activePointers.size === 1) {
                this.app.cameraState.isDragging = true;
                this.app.cameraState.lastPointer = { x: e.clientX, y: e.clientY };
                this.app.cameraState.startPointer = { x: e.clientX, y: e.clientY };
                this.app.cameraState.dragStartTime = Date.now();
                container.style.cursor = 'grabbing';
            }
            container.setPointerCapture(e.pointerId);
        });

        container.addEventListener('pointermove', (e) => {
            if (activePointers.has(e.pointerId)) {
                activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
                if (activePointers.size >= 1) e.preventDefault();
            }

            if (activePointers.size === 2) {
                // Handle Pinch Zoom and Pan
                const ptrs = Array.from(activePointers.values());
                const currentDistance = Math.hypot(ptrs[0].x - ptrs[1].x, ptrs[0].y - ptrs[1].y);
                const currentMidpoint = { x: (ptrs[0].x + ptrs[1].x) / 2, y: (ptrs[0].y + ptrs[1].y) / 2 };

                if (initialPinchDistance > 0) {
                    const zoomFactor = currentDistance / initialPinchDistance;
                    let newZoom = Math.max(0.5, Math.min(initialPinchZoom * zoomFactor, 8));
                    this.app.cameraState.zoom = newZoom;

                    const dx = currentMidpoint.x - lastMidpoint.x;
                    const dy = currentMidpoint.y - lastMidpoint.y;
                    this.app.cameraState.offset.x += dx;
                    this.app.cameraState.offset.y += dy;
                    lastMidpoint = currentMidpoint;

                    requestAnimationFrame(this.resizeHandle);
                }
            } else if (this.app.cameraState.isDragging && activePointers.size === 1) {
                // Handle Pan
                const dx = e.clientX - this.app.cameraState.lastPointer.x;
                const dy = e.clientY - this.app.cameraState.lastPointer.y;

                this.app.cameraState.offset.x += dx;
                this.app.cameraState.offset.y += dy;
                this.app.cameraState.lastPointer = { x: e.clientX, y: e.clientY };

                requestAnimationFrame(this.resizeHandle);
            }
        });

        const onPointerUp = (e: PointerEvent) => {
            activePointers.delete(e.pointerId);

            if (activePointers.size < 2) {
                initialPinchDistance = 0;
            }

            if (activePointers.size === 0) {
                this.app.cameraState.isDragging = false;
                container.releasePointerCapture(e.pointerId);
                container.style.cursor = 'grab';

                const dist = Math.hypot(e.clientX - this.app.cameraState.startPointer.x, e.clientY - this.app.cameraState.startPointer.y);
                const duration = Date.now() - this.app.cameraState.dragStartTime;

                if (dist < 5 && duration < 300) {
                    this.app.cameraState.offset = { x: 0, y: 0 };
                    this.app.cameraState.zoom = 1.0;
                    requestAnimationFrame(this.resizeHandle);
                }
            } else if (activePointers.size === 1) {
                const remainingPtr = Array.from(activePointers.values())[0];
                this.app.cameraState.isDragging = true;
                this.app.cameraState.lastPointer = { x: remainingPtr.x, y: remainingPtr.y };
            }
        };

        container.addEventListener('pointerup', onPointerUp);
        container.addEventListener('pointercancel', onPointerUp);

        container.addEventListener('wheel', (e) => {
            e.preventDefault();
            const zoomSpeed = 0.001;
            const delta = -e.deltaY;
            const factor = 1 + (delta * zoomSpeed);
            let newZoom = Math.max(0.5, Math.min(this.app.cameraState.zoom * factor, 8));
            this.app.cameraState.zoom = newZoom;
            requestAnimationFrame(this.resizeHandle);
        }, { passive: false });
    }
}


